import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import UraSolicitacoes from "@/components/UraSolicitacoes";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageCircle, Send, Search, Plus, Settings, Check, CheckCheck, Clock, Users,
  Zap, Tag, RefreshCw, Trash2, BarChart3, QrCode, WifiOff, UserPlus, AlertCircle,
  ArrowRightLeft, X, MoreHorizontal, Edit2, Columns, List, Archive, Filter,
  GitBranch, Smile, Mic, MicOff, Paperclip, PauseCircle, PlayCircle, FileText,
  Image as ImageIcon, Volume2, StopCircle, Loader2, CalendarClock, CalendarDays,
  ChevronLeft, ChevronRight, ChevronDown, CornerUpLeft, Bold, Italic, Strikethrough, XCircle, User,
  MousePointerClick, ListOrdered, Pencil, ListFilter, FileSearch, Edit, FilePlus2, Database, Phone, Briefcase, Maximize2
} from "lucide-react";
import EmojiPicker from "emoji-picker-react";

// URL absoluta da midia: o backend grava caminhos relativos (/api/uploads/...)
const mediaSrc = (url) => ((url || "").startsWith("http") ? url : `${API.replace("/api", "")}${url}`);
import FlowBuilder from "./FlowBuilder";
import GestaoConexoesWhatsApp from "./GestaoConexoesWhatsApp";
import ProspectaSimulacaoModal from "./ProspectaSimulacaoModal";

const fmt = (d) => { if (!d) return ""; try { const dt = new Date(d); const now = new Date(); const diff = now - dt; if (diff < 86400000) return dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); if (diff < 172800000) return "Ontem"; return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }); } catch { return ""; } };
const fmtAgendaFull = (d) => { if (!d) return "—"; const dt = new Date(d); if (isNaN(dt.getTime())) return "—"; return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
const etapaLabel = (nome) => nome === "Processo Finalizado" ? "Simulação Concluída" : nome;
const formatCpf = (cpf) => {
  const digits = String(cpf || "").replace(/\D/g, "").padStart(11, "0").slice(-11);
  if (!digits || digits === "00000000000") return "—";
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};
const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  const raw = String(dataNascimento).trim();
  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const nascimento = br
    ? new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]))
    : iso
      ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
      : new Date(raw);
  if (Number.isNaN(nascimento.getTime())) return null;
  const hoje = new Date();
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const aniversarioAindaNaoChegou = hoje.getMonth() < nascimento.getMonth()
    || (hoje.getMonth() === nascimento.getMonth() && hoje.getDate() < nascimento.getDate());
  if (aniversarioAindaNaoChegou) idade -= 1;
  return idade >= 0 && idade <= 130 ? idade : null;
};

// Estes resultados sao etiquetas pesquisaveis, nao etapas exclusivas do funil.
// O backend ainda pode devolver o valor antigo em tag_gestao_nome; nesse caso
// ele aparece como tag comum ate a migracao persistente terminar.
const RESULTADO_TAGS = [
  { nome: "COM VALORES", cor: "#0D9488" },
  { nome: "SEM VALORES", cor: "#BE123C" },
  { nome: "MARGEM NEGATIVA", cor: "#DC2626" },
  { nome: "ACEITOU A OFERTA", cor: "#047857" },
  { nome: "SEM INTERESSE", cor: "#737373" },
];
const RESULTADO_TAG_NAMES = new Set(RESULTADO_TAGS.map(t => t.nome));
const prepararTicketResultado = (ticket) => {
  const legado = String(ticket?.tag_gestao_nome || "").trim().toUpperCase();
  if (!RESULTADO_TAG_NAMES.has(legado)) return ticket;
  const tagsAtuais = (ticket.tags || []).map(t => String(t).trim().toUpperCase());
  if (tagsAtuais.includes(legado)) return ticket;
  return { ...ticket, tags: [...(ticket.tags || []), legado] };
};

const fmtDuracao = (seg) => {
  if (!seg || seg <= 0) return "0s";
  const d = Math.floor(seg / 86400), h = Math.floor((seg % 86400) / 3600), m = Math.floor((seg % 3600) / 60), s = seg % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s}s`;
};

// Detect if a phone number looks like an unresolved WhatsApp LID
function isLidNumber(phone) {
  if (!phone) return false;
  const clean = phone.replace(/\D/g, "");
  // Brazilian numbers: 12-13 digits starting with 55
  // LID numbers: typically 13-16+ digits, not starting with common country codes
  if (clean.length > 14) return true;
  if (clean.length >= 12 && !clean.startsWith("55")) return true;
  return false;
}

function Avatar({ nome, size = "md" }) {
  const s = size === "sm" ? "h-7 w-7 text-[10px]" : size === "lg" ? "h-12 w-12 text-lg" : "h-9 w-9 text-xs";
  const colors = ["bg-blue-500","bg-green-500","bg-purple-500","bg-orange-500","bg-pink-500","bg-teal-500","bg-indigo-500","bg-rose-500"];
  const c = colors[(nome || "").charCodeAt(0) % colors.length];
  return <div className={`${s} ${c} rounded-full flex items-center justify-center text-white font-bold shrink-0`}>{(nome || "?")[0].toUpperCase()}</div>;
}

function ticketEstaEmNovasMensagens(ticket) {
  return ticket?.status !== "FINALIZADO" && (
    ticket?.entrada_pendente === true ||
    (ticket?.entrada_pendente == null && (ticket?.nao_lidas || 0) > 0)
  );
}

// Novas mensagens e uma fila exclusiva. Enquanto houver uma chegada pendente,
// o atendimento nao pode aparecer simultaneamente em outra fila operacional.
function ticketPertenceAoStatusOperacional(ticket, status) {
  if (ticketEstaEmNovasMensagens(ticket)) return false;
  if (status === "FLUXO_ROBO") {
    return ticket?.flow_state?.active === true && ticket?.status !== "FINALIZADO";
  }
  return ticket?.status === status;
}

function StatusBadge({ status, chatTabs, ticketId, ticketIds = [], onChangeStatus, flowActive = false, permitirStatusAtual = false, emNovasMensagens = false }) {
  const ct = emNovasMensagens
    ? chatTabs.find(c => c.key === "ENTRADA")
    : flowActive
    ? chatTabs.find(c => c.key === "FLUXO_ROBO")
    : chatTabs.find(c => c.key === status);
  const [open, setOpen] = useState(false);
  if (!ct) return null;
  const badge = (
    <span
      data-testid="status-badge"
      className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${onChangeStatus ? "cursor-pointer hover:opacity-80 select-none" : ""}`}
      style={{ backgroundColor: ct.color + "20", color: ct.color, border: `1px solid ${ct.color}40` }}
      onClick={onChangeStatus ? (e) => { e.stopPropagation(); setOpen(o => !o); } : undefined}
      title={onChangeStatus ? "Clique para mover para outra aba" : undefined}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: ct.color }} />
      {ct.label}
      {onChangeStatus && <ChevronDown className="h-2.5 w-2.5 opacity-60" />}
    </span>
  );
  if (!onChangeStatus) return badge;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{badge}</PopoverTrigger>
      <PopoverContent side="top" sideOffset={6} collisionPadding={8} align="start" className="w-52 max-h-[min(22rem,var(--radix-popover-content-available-height))] overflow-y-auto p-2 z-[100]" onClick={e => e.stopPropagation()}>
        <p className="text-[11px] text-gray-500 font-semibold mb-1 px-1">Mover para:</p>
        <div className="flex flex-col">
          {chatTabs.filter(c => (permitirStatusAtual || c.key !== status) && !["ENTRADA", "ABERTOS_TODOS", "TODOS_GERAL", "CHAT_WEB", "URA_SOLICITACOES", "PABX"].includes(c.key) && !c.key.startsWith("PABX_")).map(c => (
            <button
              key={c.key}
              className="flex items-center gap-2 w-full px-2 py-1.5 rounded text-[12px] hover:bg-gray-100 text-left"
              data-testid={`badge-mover-para-${c.key}-${ticketId}`}
              onClick={(e) => { e.stopPropagation(); onChangeStatus(ticketIds.length ? ticketIds : [ticketId], c.key); setOpen(false); }}>
              <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TicketTimer({ inicioSessao, status }) {
  const isStopped = !inicioSessao || status === "FINALIZADO" || status === "RESOLVIDO";
  const [label, setLabel] = useState("");
  useEffect(() => {
    if (!inicioSessao) { setLabel(""); return; }
    const calc = () => {
      const end = isStopped ? Date.now() : Date.now();
      const diff = Math.max(0, Math.floor((end - new Date(inicioSessao).getTime()) / 1000));
      const d = Math.floor(diff / 86400);
      const h = Math.floor((diff % 86400) / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      if (d > 0) setLabel(`${d}d ${h}h ${m}m`);
      else if (h > 0) setLabel(`${h}h ${m}m ${s}s`);
      else setLabel(`${m}m ${s}s`);
    };
    calc();
    if (isStopped) return;
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [inicioSessao, isStopped]);
  if (!inicioSessao) return null;
  return (
    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${isStopped ? "bg-gray-100 text-gray-400" : "bg-green-50 border border-green-200 text-green-700"}`} data-testid="ticket-timer">
      <Clock className="h-3 w-3" />
      <span>{label}</span>
    </div>
  );
}

function AudioPlayer({ src, transcription, onTranscribe, isTranscribing }) {
  return (
    <div className="space-y-1">
      <audio controls className="h-8 max-w-[240px]" preload="metadata">
        <source src={src} />
      </audio>
      {transcription ? (
        <p className="text-[10px] text-gray-500 italic bg-gray-50 px-2 py-1 rounded" data-testid="audio-transcription">
          <Volume2 className="h-2.5 w-2.5 inline mr-1" />{transcription}
        </p>
      ) : onTranscribe ? (
        <button onClick={onTranscribe} disabled={isTranscribing} className="text-[9px] text-blue-500 hover:text-blue-700 flex items-center gap-0.5" data-testid="btn-transcribe">
          {isTranscribing ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Volume2 className="h-2.5 w-2.5" />}
          {isTranscribing ? "Transcrevendo..." : "Transcrever"}
        </button>
      ) : null}
    </div>
  );
}

function MsgStatusIcon({ msg }) {
  const st = msg.envio_status;
  if (st === "enviado") return <CheckCheck className="h-2.5 w-2.5 text-blue-400" data-testid={`msg-status-enviado-${msg.id}`} />;
  if (st === "falha") return <span title={msg.envio_erro || "Falha no envio"} data-testid={`msg-status-falha-${msg.id}`}><AlertCircle className="h-2.5 w-2.5 text-red-500" /></span>;
  if (st === "sessao_desconectada") return <span title="Sessao WhatsApp desconectada" data-testid={`msg-status-desconectada-${msg.id}`}><AlertCircle className="h-2.5 w-2.5 text-amber-500" /></span>;
  return null;
}

const CHANNEL_META = {
  WHATSAPP: { label: "WhatsApp", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  WEBCHAT: { label: "Web Chat", className: "bg-blue-50 text-blue-700 border-blue-200" },
  PABX_URA: { label: "PABX / URA", className: "bg-violet-50 text-violet-700 border-violet-200" },
  INSTAGRAM: { label: "Instagram", className: "bg-pink-50 text-pink-700 border-pink-200" },
  FACEBOOK: { label: "Facebook", className: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  SMS: { label: "SMS", className: "bg-amber-50 text-amber-700 border-amber-200" },
  EMAIL: { label: "E-mail", className: "bg-slate-50 text-slate-700 border-slate-200" },
  TELEFONE: { label: "Telefone", className: "bg-cyan-50 text-cyan-700 border-cyan-200" },
};

const ticketChannel = (ticket) => {
  if (ticket?.origem === "CHAT_WEB" || ticket?.canal === "web") return "WEBCHAT";
  if (ticket?.origem === "PABX") return "PABX_URA";
  return String(ticket?.origem || "WHATSAPP").toUpperCase();
};

function ChannelBadge({ channel, label }) {
  if (!channel) return null;
  const meta = CHANNEL_META[channel] || { label: channel, className: "bg-gray-50 text-gray-600 border-gray-200" };
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${meta.className}`}
      data-testid={`message-channel-${channel}`}>
      {label || meta.label}
    </span>
  );
}

function MessageBubble({ msg, onTranscribe, transcriptions, onReply }) {
  const isOut = msg.direcao === "saida";
  if (msg.direcao === "sistema" || msg.tipo === "sistema") {
    return (
      <div className="flex justify-center my-2">
        <span className="text-[10px] text-gray-400 bg-gray-100 px-3 py-1 rounded-full flex items-center gap-2">
          <ChannelBadge channel={msg.canal} label={msg.canal_label} />
          {msg.flow_nome ? <><GitBranch className="h-2.5 w-2.5 inline mr-1" />{msg.corpo}</> : msg.corpo}
        </span>
      </div>
    );
  }

  const isAudio = msg.tipo === "audio" && msg.media_url;
  const isImage = msg.tipo === "imagem" && msg.media_url;
  const isVideo = msg.tipo === "video" && msg.media_url;
  const isDoc = msg.tipo === "documento" && msg.media_url;
  const isBotoes = msg.tipo === "botoes";
  const transcription = msg.transcricao || transcriptions?.[msg.id];
  const isTranscribing = transcriptions?.[`loading_${msg.id}`];

  if (isBotoes) {
    return (
      <div className={`flex ${isOut ? "justify-end" : "justify-start"} mb-1`} data-testid={`msg-botoes-${msg.id}`}>
        <div className={`max-w-[75%] rounded-2xl text-sm leading-relaxed overflow-hidden ${isOut ? "bg-[#d9fdd3] rounded-tr-sm" : "bg-white border rounded-tl-sm shadow-sm"}`}>
          <div className="px-3 py-1.5">
            {msg.canal && <div className="mb-1"><ChannelBadge channel={msg.canal} label={msg.canal_label} /></div>}
            {!isOut && msg.remetente_nome && <p className="text-[11px] font-semibold text-blue-600 mb-0.5">{msg.remetente_nome}</p>}
            {msg.corpo && <p className="whitespace-pre-wrap break-words">{msg.corpo}</p>}
            {msg.footer && <p className="text-[10px] text-gray-400 mt-1">{msg.footer}</p>}
            <div className={`flex items-center gap-1 mt-0.5 ${isOut ? "justify-end" : ""}`}>
              <span className="text-[9px] text-gray-400">{fmt(msg.created_at)}</span>
              {isOut && <MsgStatusIcon msg={msg} />}
            </div>
          </div>
          {(msg.buttons || []).length > 0 && (
            <div className="border-t">
              {msg.buttons.map((btn, i) => (
                <div key={i} className={`text-center py-2 px-3 text-[13px] font-medium text-[#00a5f4] cursor-default ${i < msg.buttons.length - 1 ? "border-b" : ""}`} data-testid={`msg-btn-${i}`}>
                  <MousePointerClick className="h-3 w-3 inline mr-1.5 opacity-60" />
                  {typeof btn === "string" ? btn : btn.label || btn.displayText || `Opcao ${i + 1}`}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"} mb-1 group`}>
      {/* Botao Responder aparece no hover (ordem troca conforme direcao) */}
      {isOut && onReply && (
        <button
          onClick={() => onReply(msg)}
          className="self-center mx-1 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 flex items-center justify-center rounded-full bg-white border shadow hover:bg-gray-50 text-gray-500 hover:text-green-600"
          title="Responder esta mensagem"
          data-testid={`btn-reply-${msg.id}`}
        >
          <CornerUpLeft className="h-3.5 w-3.5" />
        </button>
      )}
      <div className={`max-w-[75%] px-3 py-1.5 rounded-2xl text-sm leading-relaxed ${isOut ? "bg-[#d9fdd3] rounded-tr-sm" : "bg-white border rounded-tl-sm shadow-sm"}`}>
        {msg.canal && <div className="mb-1"><ChannelBadge channel={msg.canal} label={msg.canal_label} /></div>}
        {!isOut && msg.remetente_nome && <p className="text-[11px] font-semibold text-blue-600 mb-0.5">{msg.remetente_nome}</p>}
        {msg.quoted_corpo && (
          <div className={`border-l-4 ${isOut ? "border-green-600 bg-green-50" : "border-blue-500 bg-gray-50"} px-2 py-1 rounded mb-1 text-[11px]`}>
            {msg.quoted_remetente && <p className="font-semibold text-blue-700">{msg.quoted_remetente}</p>}
            <p className="text-gray-600 line-clamp-2 italic">{msg.quoted_corpo}</p>
          </div>
        )}
        {isImage && (
          <div className="mb-1">
            <img src={mediaSrc(msg.media_url)} alt="" className="max-w-[240px] rounded-lg cursor-pointer" onClick={() => window.open(mediaSrc(msg.media_url), "_blank")} />
          </div>
        )}
        {isVideo && (
          <div className="mb-1 relative w-[240px] max-w-full">
            <video
              src={mediaSrc(msg.media_url)}
              controls
              preload="metadata"
              playsInline
              className="w-full rounded-lg bg-black"
              data-testid={`msg-video-${msg.id}`}
            />
            <button
              onClick={() => window.open(mediaSrc(msg.media_url), "_blank")}
              title="Abrir vídeo em nova aba"
              className="absolute top-1.5 right-1.5 h-6 w-6 flex items-center justify-center rounded-md bg-black/55 text-white hover:bg-black/75 transition-colors"
              data-testid={`msg-video-open-${msg.id}`}
            >
              <Maximize2 className="h-3 w-3" />
            </button>
          </div>
        )}
        {isAudio && (
          <AudioPlayer
            src={mediaSrc(msg.media_url)}
            transcription={transcription}
            onTranscribe={() => onTranscribe(msg)}
            isTranscribing={isTranscribing}
          />
        )}
        {isDoc && (
          <a href={mediaSrc(msg.media_url)}
             target="_blank" rel="noopener noreferrer"
             className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 mb-1">
            <FileText className="h-5 w-5 text-blue-500" />
            <span className="text-xs text-blue-600 underline">{msg.media_filename || "Documento"}</span>
          </a>
        )}
        {msg.corpo && <p className="whitespace-pre-wrap break-words">{msg.corpo}</p>}
        <div className={`flex items-center gap-1 mt-0.5 ${isOut ? "justify-end" : ""}`}>
          <span className="text-[9px] text-gray-400">{fmt(msg.created_at)}</span>
          {isOut && <MsgStatusIcon msg={msg} />}
        </div>
      </div>
      {!isOut && onReply && (
        <button
          onClick={() => onReply(msg)}
          className="self-center mx-1 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 flex items-center justify-center rounded-full bg-white border shadow hover:bg-gray-50 text-gray-500 hover:text-green-600"
          title="Responder esta mensagem"
          data-testid={`btn-reply-${msg.id}`}
        >
          <CornerUpLeft className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function TicketCard({ ticket, tags, filas, onSelect, onResolve, onAccept, onAddTag, onRemoveTag }) {
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [localTagSearch, setLocalTagSearch] = useState("");
  const fila = filas.find(f => f.id === ticket.fila_id);
  return (
    <div className="bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer mb-2" onClick={() => onSelect(ticket)} data-testid={`kanban-card-${ticket.id}`}>
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Avatar nome={ticket.contato_nome} size="sm" />
            <div>
              <p className="font-semibold text-sm leading-tight">{ticket.contato_nome || ticket.contato_telefone}</p>
              <p className="text-[10px] text-gray-400 flex items-center gap-1">
                {ticket.is_lid && isLidNumber(ticket.contato_telefone)
                  ? <span className="text-amber-500 font-bold">LID</span>
                  : ticket.contato_telefone}
              </p>
            </div>
          </div>
          <span className="text-[10px] text-gray-400">{fmt(ticket.updated_at)}</span>
          {ticket.nao_lidas > 0 && (
            <span className="bg-green-500 text-white text-[10px] font-bold min-w-[20px] h-5 flex items-center justify-center rounded-full animate-pulse shadow-sm">{ticket.nao_lidas}</span>
          )}
        </div>
        {ticket.ultima_mensagem && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{ticket.ultima_mensagem}</p>}
        <div className="flex flex-wrap gap-1 mb-2">
          {(ticket.tags || []).map(t => {
            const tagObj = tags.find(tg => tg.nome === t);
            return (
              <span key={t} className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: tagObj?.cor || "#8b5cf6" }}>
                {t}
                <button className="hover:opacity-70" onClick={e => { e.stopPropagation(); onRemoveTag(ticket.id, t); }}><X className="h-2.5 w-2.5" /></button>
              </span>
            );
          })}
          <button className="text-[10px] text-gray-400 hover:text-purple-600 px-1 py-0.5 rounded border border-dashed border-gray-300 hover:border-purple-400 transition-colors"
            onClick={e => { e.stopPropagation(); setShowTagPicker(!showTagPicker); }}>
            <Tag className="h-2.5 w-2.5 inline mr-0.5" />+
          </button>
        </div>
        {showTagPicker && (
          <div className="mb-2 p-1.5 bg-gray-50 rounded border" onClick={e => e.stopPropagation()}>
            <input type="text" className="w-full text-[10px] border rounded px-2 py-1 mb-1 focus:outline-none focus:border-blue-400" placeholder="Buscar tag..." value={localTagSearch} onChange={e => setLocalTagSearch(e.target.value)} autoFocus />
            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
            {(() => {
              const available = tags.filter(t => !(ticket.tags || []).includes(t.nome) && (!localTagSearch || t.nome.toLowerCase().includes(localTagSearch.toLowerCase())));
              return available.length === 0 ? (
                <span className="text-[10px] text-gray-400">{localTagSearch ? "Nenhuma encontrada" : "Todas tags atribuidas"}</span>
              ) : available.map(t => (
                <button key={t.id} className="text-[10px] font-bold px-1.5 py-0.5 rounded hover:opacity-80 transition-opacity text-white" style={{ backgroundColor: t.cor }}
                  onClick={() => { onAddTag(ticket.id, t.nome); setShowTagPicker(false); setLocalTagSearch(""); }}>
                  {t.nome}
                </button>
              ));
            })()}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between pt-1 border-t">
          <div className="flex items-center gap-1">
            {fila && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: fila.cor + "15", color: fila.cor }}>{fila.nome}</span>}
            {ticket.nao_lidas > 0 && <span className="bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">{ticket.nao_lidas}</span>}
          </div>
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            {ticket.status === "ABERTO" && (
              <Button size="sm" className="h-6 text-[10px] px-2 bg-green-600 hover:bg-green-700 text-white" onClick={() => onAccept(ticket.id)}>ACEITAR</Button>
            )}
            {ticket.status !== "RESOLVIDO" && (
              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-green-600 border-green-300" onClick={() => onResolve(ticket.id)}>FINALIZAR</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Mescla o lote recente de tickets preservando os da aba ativa que ficaram fora dele.// A aba nao e sempre um status: CHAT_WEB/PABX filtram por origem, FLUXO_ROBO por flow_state.
const mergeTickets = (prev, fresh, tab) => {
  const preparados = (fresh || []).map(prepararTicketResultado);
  if (!tab || tab === "ABERTOS_TODOS" || tab === "TODOS_GERAL") return preparados;
  const keep = tab === "ENTRADA" ? (t) => ticketEstaEmNovasMensagens(t)
    : tab === "FLUXO_ROBO" ? (t) => ticketPertenceAoStatusOperacional(t, "FLUXO_ROBO")
    : tab === "CHAT_WEB" ? (t) => t.origem === "CHAT_WEB"
    : (tab === "PABX" || tab.startsWith("PABX_")) ? (t) => t.origem === "PABX"
    : (t) => ticketPertenceAoStatusOperacional(t, tab);
  const freshIds = new Set(preparados.map(t => t.id));
  return [...preparados, ...prev.filter(t => keep(t) && !freshIds.has(t.id))];
};

// Na lista lateral, tickets vinculados ao mesmo cliente viram um unico
// atendimento. Os tickets continuam preservados para transporte e auditoria.
const agruparAtendimentosCliente = (items) => {
  const grupos = new Map();
  (items || []).forEach(ticket => {
    const cpf = String(ticket.cpf_contato || ticket.pabx_data?.cpf || "").replace(/\D/g, "");
    const chave = ticket.cliente_id
      ? `cliente:${ticket.cliente_id}`
      : (cpf.length === 11 ? `cpf:${cpf}` : `ticket:${ticket.id}`);
    const canal = ticketChannel(ticket);
    const atual = grupos.get(chave);
    if (!atual) {
      grupos.set(chave, {
        ...ticket,
        contato_nome: ticket.cliente_nome_vinculado || ticket.contato_nome,
        nao_lidas: Number(ticket.nao_lidas || 0),
        // Compatibilidade com conversas antigas: antes de existir
        // entrada_pendente, a chegada era identificada por nao_lidas.
        entrada_pendente: ticketEstaEmNovasMensagens(ticket),
        canais_atendimento: [...new Set([...(ticket.canais_atendimento || []), canal])],
        tickets_atendimento: [ticket.id],
      });
      return;
    }
    const atualTs = new Date(atual.updated_at || atual.created_at || 0).getTime() || 0;
    const novoTs = new Date(ticket.updated_at || ticket.created_at || 0).getTime() || 0;
    const representante = novoTs > atualTs ? { ...atual, ...ticket } : atual;
    grupos.set(chave, {
      ...representante,
      contato_nome: representante.cliente_nome_vinculado || atual.cliente_nome_vinculado || ticket.cliente_nome_vinculado || representante.contato_nome,
      nao_lidas: Number(atual.nao_lidas || 0) + Number(ticket.nao_lidas || 0),
      entrada_pendente: ticketEstaEmNovasMensagens(atual) || ticketEstaEmNovasMensagens(ticket),
      canais_atendimento: [...new Set([...(atual.canais_atendimento || []), canal])],
      tickets_atendimento: [...new Set([...(atual.tickets_atendimento || []), ticket.id])],
    });
  });
  return [...grupos.values()];
};

// Ordem operacional: primeiro quem acabou de falar e ainda nao foi lido;
// dentro de cada grupo, a conversa atualizada mais recentemente fica no topo.
const ordenarPorChegada = (items, filaEntrada = false) => [...items].sort((a, b) => {
  if (filaEntrada) {
    const timeA = new Date(a.entrada_desde || a.updated_at || a.created_at || 0).getTime() || 0;
    const timeB = new Date(b.entrada_desde || b.updated_at || b.created_at || 0).getTime() || 0;
    if (timeA !== timeB) return timeA - timeB;
    return String(a.contato_nome || a.contato_telefone || "").localeCompare(String(b.contato_nome || b.contato_telefone || ""), "pt-BR");
  }
  const unreadDiff = Number((b.nao_lidas || 0) > 0) - Number((a.nao_lidas || 0) > 0);
  if (unreadDiff !== 0) return unreadDiff;
  const timeA = new Date(a.updated_at || a.created_at || 0).getTime() || 0;
  const timeB = new Date(b.updated_at || b.created_at || 0).getTime() || 0;
  if (timeA !== timeB) return timeB - timeA;
  return String(a.contato_nome || a.contato_telefone || "").localeCompare(String(b.contato_nome || b.contato_telefone || ""), "pt-BR");
});

export default function AtendimentoWhatsApp() {
  const [tickets, setTickets] = useState([]);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [filas, setFilas] = useState([]);
  const [tags, setTags] = useState([]);
  const [respostas, setRespostas] = useState([]);
  const [mensagens, setMensagens] = useState([]);
  const [flows, setFlows] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [busca, setBusca] = useState("");
  const [buscaTickets, setBuscaTickets] = useState([]);
  const [buscaCarregando, setBuscaCarregando] = useState(false);
  const [filtroTag, setFiltroTag] = useState("todos");
  const [funilFiltro, setFunilFiltro] = useState(null);
  const [vendaFiltro, setVendaFiltro] = useState(null);
  const [vendaTickets, setVendaTickets] = useState([]);
  const [vendaCounts, setVendaCounts] = useState({});
  const [funilTickets, setFunilTickets] = useState([]);
  const [funilCounts, setFunilCounts] = useState({});
  const [funilPickerFor, setFunilPickerFor] = useState(null);
  const [filtroFila, setFiltroFila] = useState("todos");
  const [msgInput, setMsgInput] = useState("");
  const [enviandoMensagem, setEnviandoMensagem] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [tagPickerFor, setTagPickerFor] = useState(null);
  const [tab, setTab] = useState("chat");
  const [chatTab, setChatTab] = useState("ENTRADA");
  // Canal e status sao dimensoes independentes. O operador pode, por exemplo,
  // ver "Web + Aguardando" sem perder o mesmo atendimento omnichannel.
  const [canalFiltro, setCanalFiltro] = useState("TODOS");
  const [pabxSegmentoFiltro, setPabxSegmentoFiltro] = useState(null);
  // null = usar automaticamente o canal da ultima mensagem recebida do cliente.
  const [replyTargetTicketId, setReplyTargetTicketId] = useState(null);
  const [uraSolicCount, setUraSolicCount] = useState(0);
  const [showNovoTicket, setShowNovoTicket] = useState(false);
  const [showNovaFila, setShowNovaFila] = useState(false);
  const [showNovaResposta, setShowNovaResposta] = useState(false);
  const [showNovaTag, setShowNovaTag] = useState(false);
  const [showRespostas, setShowRespostas] = useState(false);
  const [showTransferir, setShowTransferir] = useState(false);
  const [showEditTag, setShowEditTag] = useState(null);
  const [novoTicketForm, setNovoTicketForm] = useState({ contato_nome: "", contato_telefone: "", fila_id: "" });
  const [novaFilaForm, setNovaFilaForm] = useState({ nome: "", cor: "#3b82f6", descricao: "" });
  const [novaRespostaForm, setNovaRespostaForm] = useState({ atalho: "", mensagem: "", categoria: "geral" });
  const [novaTagForm, setNovaTagForm] = useState({ nome: "", cor: "#8b5cf6", categoria_id: "" });
  const [kanbanSelectedTags, setKanbanSelectedTags] = useState([]);
  const [analiseBuscaTag, setAnaliseBuscaTag] = useState("");
  const [showKanbanFilter, setShowKanbanFilter] = useState(false);

  // Tag Categorias
  const [tagCategorias, setTagCategorias] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [showCategoriaManager, setShowCategoriaManager] = useState(false);
  const [novaCategoriaForm, setNovaCategoriaForm] = useState({ nome: "", cor: "#6366f1" });
  const [editCategoria, setEditCategoria] = useState(null);
  const [collapsedCats, setCollapsedCats] = useState({});


  const [tagSearchTerm, setTagSearchTerm] = useState("");
  const [clienteVinculado, setClienteVinculado] = useState(null);
  const [timelineResumo, setTimelineResumo] = useState(null);
  const [valoresDispWpp, setValoresDispWpp] = useState(null);
  useEffect(() => {
    const cpfVd = clienteVinculado?.cpf;
    if (!cpfVd) { setValoresDispWpp(null); return; }
    (async () => {
      try {
        const rvd = await axios.get(`${API}/prospecta/valores-disponiveis`, { params: { cpf: cpfVd } });
        setValoresDispWpp(rvd.data && rvd.data.tem_calculo ? rvd.data : null);
      } catch { setValoresDispWpp(null); }
    })();
  }, [clienteVinculado?.cpf]);
  const [showProspecta, setShowProspecta] = useState(false);
  const [tagsGestaoClientes, setTagsGestaoClientes] = useState([]);
  const [showTagGestaoPicker, setShowTagGestaoPicker] = useState(false);
  const [salvandoTagGestao, setSalvandoTagGestao] = useState(false);
  const msgEndRef = useRef(null);

  // New states for chat enhancements
  const [showEmoji, setShowEmoji] = useState(false);
  const [showFlowPicker, setShowFlowPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcriptions, setTranscriptions] = useState({});
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  // Baileys connection state
  const [baileysStatus, setBaileysStatus] = useState({ status: "offline", number: null });
  const [baileysQR, setBaileysQR] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);
  const qrPollRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiRef = useRef(null);

  // Buttons modal states
  const [showBotoesModal, setShowBotoesModal] = useState(false);
  const [botoesForm, setBotoesForm] = useState({ corpo: "", footer: "", buttons: [{ label: "" }, { label: "" }] });
  const [enviandoBotoes, setEnviandoBotoes] = useState(false);

  // Scheduling states
  const [agendamentos, setAgendamentos] = useState([]);
  const [showCriarAgendamento, setShowCriarAgendamento] = useState(false);
  const [agendamentoTab, setAgendamentoTab] = useState("rapidas");
  const [agendamentoForm, setAgendamentoForm] = useState({ conteudo: "", tipo: "mensagem", flow_id: "", flow_nome: "", data_agendamento: "" });
  const [agendamentosFiltro, setAgendamentosFiltro] = useState("todos");
  const [agendamentosData, setAgendamentosData] = useState(new Date().toISOString().slice(0, 10));
  const [agendamentosStatus, setAgendamentosStatus] = useState("todos");
  const [agendamentosBusca, setAgendamentosBusca] = useState("");
  const [reenviandoLote, setReenviandoLote] = useState(false);
  const [ticketAgendamentos, setTicketAgendamentos] = useState([]);
  const [ticketTimeline, setTicketTimeline] = useState(null);

  useEffect(() => { carregarTudo(); checkBaileysStatus(); }, []);

  // Busca global: o resultado deve continuar visivel como card mesmo quando
  // estiver fora do canal/status atualmente selecionado. Aceita nome, telefone
  // e CPF (com ou sem pontuacao).
  useEffect(() => {
    const termo = busca.trim();
    if (!termo) {
      setBuscaTickets([]);
      setBuscaCarregando(false);
      return;
    }

    let alive = true;
    const timer = setTimeout(async () => {
      setBuscaCarregando(true);
      try {
        const r = await axios.get(`${API}/wpp/tickets`, {
          params: { busca: termo, limit: 100 },
        });
        if (alive) setBuscaTickets(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (alive) setBuscaTickets([]);
        console.error("Erro ao buscar cliente:", e);
      } finally {
        if (alive) setBuscaCarregando(false);
      }
    }, 300);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [busca]);

  // Polling: atualizar tickets e mensagens automaticamente a cada 5 segundos
  const pollingRef = useRef(null);
  const selectedTicketRef = useRef(null);
  selectedTicketRef.current = selectedTicket;
  const chatTabRef = useRef(chatTab);
  chatTabRef.current = chatTab;

  useEffect(() => {
    const pollInterval = 2500; // 2.5s (lista de tickets + dashboard)
    
    const poll = async () => {
      try {
        // Always refresh ticket list for badges and new messages
        const [tk, db] = await Promise.all([
          axios.get(`${API}/wpp/tickets?limit=150`),
          axios.get(`${API}/wpp/dashboard`)
        ]);
        // Merge: keep tab-specific tickets that are not in the 2000 batch
        setTickets(prev => mergeTickets(prev, tk.data, chatTabRef.current));
        setDashboard(db.data);
      } catch (e) { /* ignore polling errors */ }
    };
    
    pollingRef.current = setInterval(poll, pollInterval);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  // Polling rapido da linha do tempo do cliente. Sem cliente vinculado,
  // mantem o comportamento anterior e consulta apenas a conversa aberta.
  useEffect(() => {
    if (!selectedTicket?.id) return;
    const ticketId = selectedTicket.id;
    const clienteId = clienteVinculado?.id;
    const fast = async () => {
      try {
        const r = clienteId
          ? await axios.get(`${API}/wpp/clientes/${clienteId}/timeline-omnichannel`, { params: { limit: 3000 } })
          : await axios.get(`${API}/wpp/tickets/${ticketId}/mensagens`);
        const canalAtual = ticketChannel(selectedTicket);
        const dados = clienteId
          ? (r.data.messages || [])
          : (r.data || []).map(m => ({
              ...m,
              canal: m.canal || canalAtual,
              canal_label: m.canal_label || CHANNEL_META[canalAtual]?.label || canalAtual,
            }));
        if (clienteId) {
          setTimelineResumo({
            canais: r.data.canais || [],
            total_mensagens: r.data.total_mensagens || 0,
            total_conversas: r.data.total_conversas || 0,
            resposta_padrao: r.data.resposta_padrao || null,
            conversas: r.data.conversas || [],
          });
        }
        setMensagens(prev => {
          if (dados.length !== prev.length) return dados;
          if (dados.length > 0 && prev.length > 0 && dados[dados.length-1].id !== prev[prev.length-1].id) return dados;
          return prev;
        });
      } catch { /* ignore */ }
    };
    const id = setInterval(fast, 2500);
    return () => clearInterval(id);
  }, [selectedTicket?.id, clienteVinculado?.id]);

  // Handle ?numero= parameter from ClienteForm
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const numero = params.get("numero");
    const nome = params.get("nome") || "";
    if (numero && tickets.length > 0) {
      // Gerar variantes do número para match (com/sem 55, com/sem 9)
      const limpo = numero.replace(/\D/g, "");
      const variantes = new Set([limpo]);
      if (limpo.startsWith("55") && limpo.length >= 12) variantes.add(limpo.slice(2));
      if (limpo.length === 11 && limpo[2] === "9") variantes.add(limpo.slice(0,2) + limpo.slice(3));
      if (limpo.length === 10) variantes.add(limpo.slice(0,2) + "9" + limpo.slice(2));
      for (const v of [...variantes]) {
        if (v.length >= 10 && v.length <= 11 && !v.startsWith("55")) variantes.add("55" + v);
      }

      const ticket = tickets.find(t => {
        const telTicket = (t.contato_telefone || "").replace(/\D/g, "");
        for (const v of variantes) {
          if (telTicket === v || telTicket.endsWith(v) || v.endsWith(telTicket)) return true;
        }
        return false;
      });
      if (ticket) {
        setSelectedTicket(ticket);
        setTab("chat");
      }
      // Clear URL parameter
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [tickets]);
  useEffect(() => {
    if (selectedTicket) {
      setClienteVinculado(null);
      setTimelineResumo(null);
      setReplyTargetTicketId(null);
      setReplyingTo(null);
      carregarMensagens(selectedTicket.id, null, selectedTicket);
      carregarTicketAgendamentos(selectedTicket.id);
      carregarTimeline(selectedTicket.id);
    }
  }, [selectedTicket?.id]);

  // Assim que o CPF/telefone identificar o cliente, troca para o historico
  // consolidado de todos os canais sem alterar o canal usado para responder.
  useEffect(() => {
    if (selectedTicket?.id && clienteVinculado?.id) {
      carregarMensagens(selectedTicket.id, clienteVinculado.id, selectedTicket);
    }
  }, [selectedTicket?.id, clienteVinculado?.id]);
  
  // Buscar cliente vinculado ao telefone do ticket + polling dinamico (15s)
  useEffect(() => {
    const buscarClienteVinculado = async () => {
      if (!selectedTicket || (!selectedTicket.contato_telefone && !selectedTicket.cpf_contato)) {
        setClienteVinculado(null);
        return;
      }
      try {
        const params = { telefone: selectedTicket.contato_telefone || "" };
        if (selectedTicket.cpf_contato) params.cpf = selectedTicket.cpf_contato;
        const r = await axios.get(`${API}/wpp/buscar-cliente-por-telefone`, { params });
        setClienteVinculado(r.data.found ? r.data.cliente : null);
      } catch { setClienteVinculado(null); }
    };
    buscarClienteVinculado();
    const pollInterval = setInterval(buscarClienteVinculado, 15000);
    return () => clearInterval(pollInterval);
  }, [selectedTicket?.id, selectedTicket?.contato_telefone, selectedTicket?.cpf_contato]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [mensagens]);

  // Close emoji picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false);
    };
    if (showEmoji) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showEmoji]);

  const checkBaileysStatus = async () => {
    try {
      const r = await axios.get(`${API}/wpp/baileys/status`);
      setBaileysStatus(r.data);
    } catch { setBaileysStatus({ status: "offline", number: null }); }
  };

  const startQRPolling = () => {
    if (qrPollRef.current) clearInterval(qrPollRef.current);
    setLoadingQR(true);
    // Trigger connect on baileys service
    axios.post(`${API}/wpp/baileys/reconnect`).catch(() => {});
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/wpp/baileys/qr`);
        if (r.data.status === "connected") {
          setBaileysStatus({ status: "connected", number: r.data.number });
          setBaileysQR(null);
          setLoadingQR(false);
          clearInterval(qrPollRef.current);
          qrPollRef.current = null;
          toast.success("WhatsApp conectado!");
          return;
        }
        if (r.data.qr) {
          setBaileysQR(r.data.qr);
          setLoadingQR(false);
        }
      } catch { setLoadingQR(false); }
    };
    poll();
    qrPollRef.current = setInterval(poll, 3000);
  };

  useEffect(() => { return () => { if (qrPollRef.current) clearInterval(qrPollRef.current); }; }, []);

  const disconnectBaileys = async () => {
    try {
      await axios.post(`${API}/wpp/baileys/disconnect`);
      setBaileysStatus({ status: "disconnected", number: null });
      setBaileysQR(null);
      toast.success("WhatsApp desconectado");
    } catch { toast.error("Erro"); }
  };

  const reconnectBaileys = async () => {
    try {
      await axios.post(`${API}/wpp/baileys/reconnect`);
      startQRPolling();
    } catch { toast.error("Erro"); }
  };

  const carregarTudo = async () => {
    try {
      const [tk, fl, tg, rr, db, fw, ag, tc] = await Promise.all([
        axios.get(`${API}/wpp/tickets?limit=150`), axios.get(`${API}/wpp/filas`),
        axios.get(`${API}/wpp/tags`), axios.get(`${API}/wpp/respostas-rapidas`),
        axios.get(`${API}/wpp/dashboard`), axios.get(`${API}/wpp/flows`),
        axios.get(`${API}/wpp/agendamentos`), axios.get(`${API}/wpp/tag-categorias`)
      ]);
      setTickets(prev => mergeTickets(prev, tk.data, chatTabRef.current)); setFilas(fl.data); setTags(tg.data); setRespostas(rr.data); setDashboard(db.data); setFlows(fw.data); setAgendamentos(ag.data); setTagCategorias(tc.data);
    } catch (e) { console.error(e); }
  };

  const carregarTags = async () => {
    const r = await axios.get(`${API}/wpp/tags`, { params: { _ts: Date.now() } });
    setTags(r.data);
  };

  // When tab changes, fetch status-specific tickets if loaded count < dashboard count
  useEffect(() => {
    const fetchTabTickets = async () => {
      if (chatTab === "ABERTOS_TODOS") return;
      if (chatTab === "ENTRADA") {
        try {
          const r = await axios.get(`${API}/wpp/tickets?entrada=true&limit=500`);
          setTickets(prev => mergeTickets(prev, r.data, "ENTRADA"));
        } catch (e) { console.error("Erro ao buscar a Entrada:", e); }
        return;
      }
      if (chatTab === "TODOS_GERAL") {
        try {
          const r = await axios.get(`${API}/wpp/tickets?limit=300`);
          setTickets(prev => {
            const ids = new Set(prev.map(t => t.id));
            return [...prev, ...r.data.filter(t => !ids.has(t.id))];
          });
        } catch (e) { console.error("Erro ao buscar Todos Geral:", e); }
        return;
      }
      if (chatTab === "CHAT_WEB") {
        try {
          const r = await axios.get(`${API}/wpp/tickets?origem=CHAT_WEB&limit=300`);
          setTickets(prev => {
            const ids = new Set(prev.map(t => t.id));
            return [...prev, ...r.data.filter(t => !ids.has(t.id))];
          });
        } catch (e) { console.error("Erro ao buscar Visitante Web:", e); }
        return;
      }
      if (chatTab === "PABX" || chatTab.startsWith("PABX_")) {
        try {
          const r = await axios.get(`${API}/wpp/tickets?origem=PABX&limit=500`);
          setTickets(prev => mergeTickets(prev, r.data, chatTab));
        } catch (e) { console.error("Erro ao buscar canal PABX:", e); }
        return;
      }
      if (chatTab === "URA_SOLICITACOES") return;
      const loadedCount = chatTab === "FLUXO_ROBO"
        ? tickets.filter(t => t.flow_state?.active === true).length
        : tickets.filter(t => t.status === chatTab).length;
      const dashCount = dashboardCounts[chatTab] || 0;
      if (loadedCount < dashCount) {
        try {
          const r = await axios.get(`${API}/wpp/tickets?status=${chatTab}&limit=200`);
          setTickets(prev => {
            const existingIds = new Set(prev.map(t => t.id));
            const newTickets = r.data.filter(t => !existingIds.has(t.id));
            return [...prev, ...newTickets];
          });
        } catch (e) { console.error("Erro ao buscar tickets da aba:", e); }
      }
    };
    fetchTabTickets();
  }, [chatTab]);

  // Trocar o canal nao altera mais o status. Apenas garante que os tickets
  // daquele transporte estejam carregados para combinar os dois filtros.
  useEffect(() => {
    let alive = true;
    const carregarCanal = async () => {
      try {
        let url = `${API}/wpp/tickets?limit=300`;
        if (canalFiltro === "WEB") url = `${API}/wpp/tickets?origem=CHAT_WEB&limit=300`;
        if (canalFiltro === "PABX_URA") url = `${API}/wpp/tickets?origem=PABX&limit=500`;
        const r = await axios.get(url);
        if (!alive) return;
        setTickets(prev => {
          const mapa = new Map(prev.map(t => [t.id, t]));
          (r.data || []).forEach(t => mapa.set(t.id, t));
          return [...mapa.values()];
        });
      } catch (e) { console.error("Erro ao carregar canal:", e); }
    };
    carregarCanal();
    return () => { alive = false; };
  }, [canalFiltro]);

  // Ao selecionar uma etapa do Funil, buscar TODOS os tickets daquela etapa (independente do status ativo)
  useEffect(() => {
    if (!funilFiltro) { setFunilTickets([]); return; }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/wpp/tickets?funil=${encodeURIComponent(funilFiltro)}&limit=500`);
        if (!alive) return;
        setFunilTickets(Array.isArray(r.data) ? r.data : []);
      } catch (e) { console.error("Erro ao carregar tickets do funil:", e); setFunilTickets([]); }
    })();
    return () => { alive = false; };
  }, [funilFiltro]);

  const carregarMaisTickets = async () => {
    if (carregandoMais || !chatTab) return;
    setCarregandoMais(true);
    try {
      const jaCarregados = tickets.filter(t => t.status === chatTab && t.origem !== "PABX" && t.origem !== "CHAT_WEB").length;
      const r = await axios.get(`${API}/wpp/tickets?status=${chatTab}&limit=200&skip=${jaCarregados}`);
      setTickets(prev => {
        const ids = new Set(prev.map(t => t.id));
        return [...prev, ...r.data.filter(t => !ids.has(t.id))];
      });
    } catch (e) { console.error("Erro ao carregar mais:", e); }
    setCarregandoMais(false);
  };

  const carregarMensagens = async (ticketId, clienteId = clienteVinculado?.id, ticketAtual = selectedTicket) => {
    try {
      if (clienteId) {
        const r = await axios.get(`${API}/wpp/clientes/${clienteId}/timeline-omnichannel`, { params: { limit: 3000 } });
        setMensagens(r.data.messages || []);
        setTimelineResumo({
          canais: r.data.canais || [],
          total_mensagens: r.data.total_mensagens || 0,
          total_conversas: r.data.total_conversas || 0,
          resposta_padrao: r.data.resposta_padrao || null,
          conversas: r.data.conversas || [],
        });
        return;
      }
      const r = await axios.get(`${API}/wpp/tickets/${ticketId}/mensagens`);
      const canalAtual = ticketChannel(ticketAtual);
      setMensagens((r.data || []).map(m => ({
        ...m,
        canal: m.canal || canalAtual,
        canal_label: m.canal_label || CHANNEL_META[canalAtual]?.label || canalAtual,
      })));
      setTimelineResumo(null);
    } catch {
      setMensagens([]);
      setTimelineResumo(null);
    }
  };

  // Destino da resposta: por padrao, o ticket/canal da ultima mensagem de
  // entrada do cliente. A escolha manual vale apenas ate o proximo envio.
  const replyTargetOptions = useMemo(() => {
    const porCanal = new Map();
    [...(mensagens || [])].reverse().forEach(msg => {
      if (!msg?.ticket_id || !msg?.canal || porCanal.has(msg.canal)) return;
      porCanal.set(msg.canal, {
        ticket_id: msg.ticket_id,
        canal: msg.canal,
        canal_label: msg.canal_label || CHANNEL_META[msg.canal]?.label || msg.canal,
      });
    });
    if (selectedTicket?.id) {
      const canal = ticketChannel(selectedTicket);
      if (!porCanal.has(canal)) {
        porCanal.set(canal, {
          ticket_id: selectedTicket.id,
          canal,
          canal_label: selectedTicket.origem === "PABX" && selectedTicket.pabx_data?.segmento
            ? `PABX / URA · ${selectedTicket.pabx_data.segmento}`
            : (CHANNEL_META[canal]?.label || canal),
        });
      }
    }
    return [...porCanal.values()];
  }, [mensagens, selectedTicket]);

  const ultimaEntradaCliente = useMemo(
    () => [...(mensagens || [])].reverse().find(msg => msg?.direcao === "entrada" && msg?.ticket_id) || null,
    [mensagens]
  );
  const respostaPadrao = timelineResumo?.resposta_padrao || (ultimaEntradaCliente ? {
    ticket_id: ultimaEntradaCliente.ticket_id,
    canal: ultimaEntradaCliente.canal,
    canal_label: ultimaEntradaCliente.canal_label,
  } : null);
  const replyTicketId = replyTargetTicketId || respostaPadrao?.ticket_id || selectedTicket?.id || null;
  const replyTargetMessage = [...(mensagens || [])].reverse().find(msg => msg?.ticket_id === replyTicketId);
  const replyTarget = replyTargetOptions.find(op => op.ticket_id === replyTicketId) || {
    ticket_id: replyTicketId,
    canal: replyTargetMessage?.canal || respostaPadrao?.canal || ticketChannel(selectedTicket),
    canal_label: replyTargetMessage?.canal_label || respostaPadrao?.canal_label || CHANNEL_META[ticketChannel(selectedTicket)]?.label,
  };

  const concluirEntradaLocal = (ticketIds) => {
    const ids = new Set(Array.isArray(ticketIds) ? ticketIds : [ticketIds]);
    const patch = { status: "ABERTO", entrada_pendente: false, entrada_desde: null, nao_lidas: 0 };
    setTickets(prev => prev.map(t => ids.has(t.id) ? { ...t, ...patch } : t));
    setSelectedTicket(prev => prev && (ids.has(prev.id) || (prev.tickets_atendimento || []).some(id => ids.has(id)))
      ? { ...prev, ...patch }
      : prev);
  };

  const recarregarConversaAtual = async () => {
    if (!selectedTicket?.id) return;
    await carregarMensagens(
      selectedTicket.id,
      clienteVinculado?.id || null,
      selectedTicket
    );
  };

  const marcarAtendido = async () => {
    if (!selectedTicket?.id) return;
    const ticketIds = [...new Set(selectedTicket.tickets_atendimento || [selectedTicket.id])];
    try {
      await Promise.all(ticketIds.map(ticketId => axios.post(`${API}/wpp/tickets/${ticketId}/atendido`)));
      concluirEntradaLocal(ticketIds);
      carregarTudo();
      toast.success("Cliente movido para Aguardando");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nao foi possivel marcar como atendido");
    }
  };

  const [verificandoWpp, setVerificandoWpp] = useState(false);
  const verificarWhatsApp = async (ticketId) => {
    setVerificandoWpp(true);
    try {
      const r = await axios.post(`${API}/wpp/tickets/${ticketId}/verificar-whatsapp`);
      if (r.data.deliverable) toast.success("Contato verificado no WhatsApp ✓ (LID vinculado)");
      else toast.error("Este número NÃO está no WhatsApp — não é possível enviar.");
      carregarTudo();
      if (selectedTicket?.id === ticketId) setSelectedTicket(p => ({ ...p, ...r.data }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao verificar WhatsApp"); }
    finally { setVerificandoWpp(false); }
  };
  const vincularLids = async () => {
    try {
      await axios.post(`${API}/wpp/vincular-lids?somente_abertos=true`);
      toast.success("Vinculação de LIDs iniciada em segundo plano...");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao iniciar vinculação"); }
  };

  const vincularCpfSelecionado = async (cpfInformado) => {
    const cpf = (cpfInformado || "").trim();
    if (!cpf || !selectedTicket?.id) return;
    try {
      const vinculo = await axios.post(`${API}/wpp/tickets/${selectedTicket.id}/vincular-cpf`, {
        cpf,
        origem: selectedTicket.origem,
      });
      const atualizado = {
        cpf_contato: cpf.replace(/\D/g, "").padStart(11, "0"),
        cliente_id: vinculo.data.cliente_id || undefined,
      };
      setSelectedTicket(prev => prev ? { ...prev, ...atualizado } : prev);
      setTickets(prev => prev.map(t => t.id === selectedTicket.id ? { ...t, ...atualizado } : t));
      if (!vinculo.data.found) {
        setClienteVinculado(null);
        toast.error("CPF válido, mas não existe cliente cadastrado");
        return;
      }
      const r = await axios.get(`${API}/wpp/buscar-cliente-por-telefone`, {
        params: { telefone: selectedTicket.contato_telefone || "", cpf },
      });
      if (r.data.found) {
        setClienteVinculado(r.data.cliente);
        toast.success(`Cliente vinculado: ${r.data.cliente.nome}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível vincular o CPF");
    }
  };

  const enviarMensagem = async () => {
    if (!msgInput.trim() || !selectedTicket || !replyTicketId || enviandoMensagem) return;
    const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
    // Monta corpo com citacao (formato WhatsApp: "> linha por linha")
    let corpoFinal = msgInput.trim();
    if (replyingTo) {
      const trecho = (replyingTo.corpo || "").trim().slice(0, 300) || "[mídia]";
      const quoted = trecho.split("\n").map(l => `> ${l}`).join("\n");
      const autor = replyingTo.remetente_nome || (replyingTo.direcao === "saida" ? "Você" : "Cliente");
      corpoFinal = `> *${autor}:*\n${quoted}\n\n${corpoFinal}`;
    }
    setEnviandoMensagem(true);
    try {
      await axios.post(`${API}/wpp/tickets/${replyTicketId}/mensagens`, {
        corpo: corpoFinal, direcao: "saida", tipo: "texto",
        remetente_nome: usuario.nome || "Atendente", remetente_id: usuario.id || "",
        quoted_corpo: replyingTo ? (replyingTo.corpo || "").trim().slice(0, 300) : undefined,
        quoted_remetente: replyingTo ? (replyingTo.remetente_nome || (replyingTo.direcao === "saida" ? "Você" : "Cliente")) : undefined,
        quoted_msg_id: replyingTo ? replyingTo.id : undefined,
      });
      setMsgInput(""); setReplyingTo(null); setReplyTargetTicketId(null);
      await recarregarConversaAtual();
      carregarTudo();
      toast.success(`Mensagem enviada por ${replyTarget?.canal_label || "canal selecionado"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mensagem não foi enviada. Tente novamente.");
    } finally {
      setEnviandoMensagem(false);
    }
  };

  // --- Send Buttons Message ---
  const enviarBotoes = async () => {
    if (!selectedTicket || !replyTicketId) return;
    const validBtns = botoesForm.buttons.filter(b => b.label.trim());
    if (!botoesForm.corpo.trim()) { toast.error("Escreva a mensagem dos botoes"); return; }
    if (validBtns.length < 1) { toast.error("Adicione pelo menos 1 botao"); return; }
    const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
    setEnviandoBotoes(true);
    try {
      await axios.post(`${API}/wpp/tickets/${replyTicketId}/mensagens-botoes`, {
        corpo: botoesForm.corpo.trim(),
        footer: botoesForm.footer.trim(),
        buttons: validBtns,
        remetente_nome: usuario.nome || "Atendente",
        remetente_id: usuario.id || ""
      });
      setReplyTargetTicketId(null);
      setShowBotoesModal(false);
      setBotoesForm({ corpo: "", footer: "", buttons: [{ label: "" }, { label: "" }] });
      await recarregarConversaAtual(); carregarTudo();
      toast.success("Botoes enviados!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar botoes"); }
    finally { setEnviandoBotoes(false); }
  };

  // --- File Upload & Send ---
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTicket || !replyTicketId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await axios.post(`${API}/wpp/chat/upload`, formData);
      const { url, tipo, filename } = uploadRes.data;
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      await axios.post(`${API}/wpp/tickets/${replyTicketId}/mensagens`, {
        corpo: tipo === "documento" ? filename : "",
        direcao: "saida", tipo: tipo,
        media_url: url, media_tipo: tipo, media_filename: filename,
        remetente_nome: usuario.nome || "Atendente", remetente_id: usuario.id || ""
      });
      setReplyTargetTicketId(null);
      await recarregarConversaAtual(); carregarTudo();
      toast.success("Arquivo enviado!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar arquivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // --- Audio Recording ---
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size > 0) await sendAudioBlob(blob);
      };
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch {
      toast.error("Nao foi possivel acessar o microfone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(recordingTimerRef.current);
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setRecordingTime(0);
    clearInterval(recordingTimerRef.current);
    audioChunksRef.current = [];
  };

  const sendAudioBlob = async (blob) => {
    if (!selectedTicket || !replyTicketId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", blob, "audio.webm");
      const uploadRes = await axios.post(`${API}/wpp/chat/upload`, formData);
      const { url } = uploadRes.data;
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      await axios.post(`${API}/wpp/tickets/${replyTicketId}/mensagens`, {
        corpo: "", direcao: "saida", tipo: "audio",
        media_url: url, media_tipo: "audio",
        remetente_nome: usuario.nome || "Atendente", remetente_id: usuario.id || ""
      });
      setReplyTargetTicketId(null);
      await recarregarConversaAtual(); carregarTudo();
      toast.success("Audio enviado!");
    } catch {
      toast.error("Erro ao enviar audio");
    } finally { setUploading(false); }
  };

  // --- Audio Transcription ---
  const transcribeAudio = async (msg) => {
    if (!msg.media_url) return;
    setTranscriptions(prev => ({ ...prev, [`loading_${msg.id}`]: true }));
    try {
      const audioUrl = mediaSrc(msg.media_url);
      const audioRes = await axios.get(audioUrl, { responseType: "blob" });
      const formData = new FormData();
      const ext = msg.media_url.split(".").pop() || "webm";
      formData.append("file", audioRes.data, `audio.${ext}`);
      const res = await axios.post(`${API}/wpp/transcribe`, formData);
      const text = res.data.text;
      setTranscriptions(prev => ({ ...prev, [msg.id]: text, [`loading_${msg.id}`]: false }));
      // Also save transcription to database for persistence
      try {
        await axios.put(`${API}/wpp/mensagens/${msg.id}`, { transcricao: text });
      } catch {}
    } catch {
      toast.error("Erro na transcricao");
      setTranscriptions(prev => ({ ...prev, [`loading_${msg.id}`]: false }));
    }
  };

  // --- Auto-transcribe untranscribed audio messages ---
  useEffect(() => {
    if (!mensagens || mensagens.length === 0) return;
    const untranscribed = mensagens.filter(m => 
      m.tipo === "audio" && m.media_url && !m.transcricao && !transcriptions[m.id] && !transcriptions[`loading_${m.id}`]
    );
    if (untranscribed.length === 0) return;
    // Transcribe up to 5 at a time to avoid overload
    untranscribed.slice(0, 5).forEach(msg => {
      transcribeAudio(msg);
    });
  }, [mensagens]);

  // --- Send Flow ---
  const sendFlow = async (flow) => {
    if (!selectedTicket || !replyTicketId) return;
    const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
    try {
      await axios.post(`${API}/wpp/tickets/${replyTicketId}/send-flow`, {
        flow_id: flow.id,
        remetente_nome: usuario.nome || "Atendente",
        remetente_id: usuario.id || ""
      });
      setShowFlowPicker(false);
      setReplyTargetTicketId(null);
      await recarregarConversaAtual(); carregarTudo();
      toast.success(`Fluxo "${flow.nome}" enviado!`);
    } catch { toast.error("Erro ao enviar fluxo"); }
  };

  // --- Pausar Robo ---
  const togglePausarRobo = async () => {
    if (!selectedTicket) return;
    const novoStatus = selectedTicket.status === "PAUSADO_ROBO" ? "EM_ATENDIMENTO" : "PAUSADO_ROBO";
    try {
      await axios.post(`${API}/wpp/tickets/${selectedTicket.id}/status`, { status: novoStatus });
      setSelectedTicket(p => ({ ...p, status: novoStatus }));
      carregarTudo();
      toast.success(novoStatus === "PAUSADO_ROBO" ? "Robo pausado!" : "Robo ativado!");
    } catch { toast.error("Erro"); }
  };

  // --- Agendamentos ---
  const carregarTicketAgendamentos = async (ticketId) => {
    try { const r = await axios.get(`${API}/wpp/agendamentos/ticket/${ticketId}`); setTicketAgendamentos(r.data); }
    catch { setTicketAgendamentos([]); }
  };

  const carregarTimeline = async (ticketId) => {
    try { const r = await axios.get(`${API}/wpp/tickets/${ticketId}/timeline`); setTicketTimeline(r.data); }
    catch { setTicketTimeline(null); }
  };

  const criarAgendamento = async () => {
    if (!selectedTicket) return;
    if (!agendamentoForm.data_agendamento) { toast.error("Selecione data e hora"); return; }
    if (agendamentoForm.tipo === "mensagem" && !agendamentoForm.conteudo.trim()) { toast.error("Escreva a mensagem"); return; }
    if (agendamentoForm.tipo === "fluxo" && !agendamentoForm.flow_id) { toast.error("Selecione um fluxo"); return; }
    const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
    try {
      await axios.post(`${API}/wpp/agendamentos`, {
        ticket_id: selectedTicket.id,
        tipo: agendamentoForm.tipo,
        conteudo: agendamentoForm.conteudo,
        flow_id: agendamentoForm.flow_id,
        flow_nome: agendamentoForm.flow_nome,
        data_agendamento: agendamentoForm.data_agendamento,
        atendente_nome: usuario.nome || "Atendente"
      });
      setShowCriarAgendamento(false);
      setAgendamentoForm({ conteudo: "", tipo: "mensagem", flow_id: "", flow_nome: "", data_agendamento: "" });
      setAgendamentoTab("rapidas");
      carregarTudo();
      carregarTicketAgendamentos(selectedTicket.id);
      toast.success("Agendamento criado!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar agendamento"); }
  };

  const cancelarAgendamento = async (agId) => {
    try {
      await axios.post(`${API}/wpp/agendamentos/${agId}/cancelar`);
      carregarTudo();
      if (selectedTicket) carregarTicketAgendamentos(selectedTicket.id);
      toast.success("Agendamento cancelado!");
    } catch { toast.error("Erro"); }
  };

  const excluirAgendamento = async (agId) => {
    try {
      await axios.delete(`${API}/wpp/agendamentos/${agId}`);
      carregarTudo();
      if (selectedTicket) carregarTicketAgendamentos(selectedTicket.id);
      toast.success("Agendamento excluido!");
    } catch { toast.error("Erro"); }
  };

  const enviarAgendamentoAgora = async (agId) => {
    try {
      await axios.post(`${API}/wpp/agendamentos/${agId}/enviar`);
      carregarTudo();
      if (selectedTicket) {
        await recarregarConversaAtual();
        carregarTicketAgendamentos(selectedTicket.id);
      }
      toast.success("Mensagem agendada enviada!");
    } catch { toast.error("Erro"); }
  };

  const reenviarErrosLote = async () => {
    setReenviandoLote(true);
    try {
      const params = agendamentosFiltro !== "todos" ? { periodo: agendamentosFiltro, data: agendamentosData } : {};
      const r = await axios.post(`${API}/wpp/agendamentos/reenviar-erros`, null, { params });
      const d = r.data || {};
      toast.success(`Reenvio: ${d.reenviados || 0} enviados, ${d.falharam || 0} falharam (de ${d.total || 0})`);
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao reenviar em lote"); }
    finally { setReenviandoLote(false); }
  };

  const applyFormatting = (format) => {
    const markers = { bold: "*", italic: "_", strike: "~" };
    const m = markers[format] || "";
    setAgendamentoForm(prev => ({ ...prev, conteudo: prev.conteudo + m + m }));
  };

  const insertVariable = (varName) => {
    setAgendamentoForm(prev => ({ ...prev, conteudo: prev.conteudo + `{${varName}}` }));
  };

  const criarTicket = async () => {
    try {
      if (!novoTicketForm.contato_nome || !novoTicketForm.contato_telefone) { toast.error("Preencha nome e telefone"); return; }
      const fila = filas.find(f => f.id === novoTicketForm.fila_id);
      const r = await axios.post(`${API}/wpp/tickets`, { ...novoTicketForm, fila_nome: fila?.nome || "" });
      setShowNovoTicket(false); setNovoTicketForm({ contato_nome: "", contato_telefone: "", fila_id: "" });
      setSelectedTicket(r.data); carregarTudo(); toast.success("Conversa criada!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const contatarLeadWhatsapp = async (sol) => {
    try {
      const tel = (sol.telefone || "").replace(/\D/g, "");
      if (tel.length < 10) { toast.error("Telefone inválido"); return; }
      const r = await axios.post(`${API}/wpp/tickets`, {
        contato_nome: sol.cliente_nome || sol.telefone,
        contato_telefone: tel,
        fila_nome: sol.fila_nome || "",
      });
      setChatTab("ABERTO");
      setSelectedTicket(r.data); carregarTudo(); toast.success("Conversa criada! Contate o lead.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar conversa"); }
  };

  useEffect(() => {
    const carregarUraCount = () => {
      axios.get(`${API}/voip/receptivo/solicitacoes/count`).then(r => setUraSolicCount(r.data?.total || 0)).catch(() => {});
    };
    carregarUraCount();
    const iv = setInterval(carregarUraCount, 60000);
    return () => clearInterval(iv);
  }, []);

  const resolverTicket = async (id) => {
    try { await axios.post(`${API}/wpp/tickets/${id}/resolver`);
      // Some da lista imediatamente: atualiza status local (o filtro por aba oculta o FINALIZADO)
      setTickets(prev => prev.map(t => t.id === id ? { ...t, status: "FINALIZADO", flow_state: { ...(t.flow_state || {}), active: false } } : t));
      if (selectedTicket?.id === id) { setSelectedTicket(null); setMensagens([]); }
      carregarTudo(); toast.success("Finalizado!");
    } catch { toast.error("Erro"); }
  };

  const carregarTagsGestao = async () => {
    try {
      const r = await axios.get(`${API}/tags-gestao-clientes`);
      setTagsGestaoClientes(r.data || []);
    } catch (e) {}
  };

  useEffect(() => { carregarTagsGestao(); }, []);

  const refreshFunilCounts = () => {
    axios.get(`${API}/wpp/funil-counts`).then(r => setFunilCounts(r.data?.counts || {})).catch(() => {});
    axios.get(`${API}/wpp/venda-counts`).then(r => setVendaCounts(r.data?.counts || {})).catch(() => {});
  };

  const alterarTagGestao = async (tagId) => {
    if (!clienteVinculado?.id) return;
    setSalvandoTagGestao(true);
    try {
      const r = await axios.patch(`${API}/clientes/${clienteVinculado.id}/tag-gestao`, { tag_gestao_id: tagId });
      // Recarregar cliente vinculado
      const tag = tagsGestaoClientes.find(t => t.id === tagId);
      setClienteVinculado(prev => prev ? { ...prev, tag_gestao_id: tagId, tag_gestao_nome: tag?.nome || prev.tag_gestao_nome } : prev);
      if (selectedTicket?.id) setTickets(prev => prev.map(t => t.id === selectedTicket.id ? { ...t, tag_gestao_id: tagId, tag_gestao_nome: tag?.nome } : t));
      refreshFunilCounts();
      toast.success(`Tag alterada para: ${tag?.nome || ""}`);
      setShowTagGestaoPicker(false);
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally {
      setSalvandoTagGestao(false);
    }
  };

  // Altera a etapa do funil (tag de gestão) direto pelo card da lista lateral
  const alterarTagGestaoTicket = async (ticket, stage) => {
    const tagObj = tagsGestaoClientes.find(t => t.nome === stage.nome);
    if (!tagObj) { toast.error("Etapa de funil não encontrada"); return; }
    try {
      await axios.patch(`${API}/wpp/tickets/${ticket.id}/tag-gestao`, { tag_gestao_id: tagObj.id });
      setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, tag_gestao_id: tagObj.id, tag_gestao_nome: stage.nome } : t));
      setSelectedTicket(prev => prev?.id === ticket.id ? { ...prev, tag_gestao_id: tagObj.id, tag_gestao_nome: stage.nome } : prev);
      // Com filtro de funil ativo a lista vem de funilTickets: atualizar/remover de la tambem (senao o card trava)
      setFunilTickets(prev => {
        if (!funilFiltro) return prev;
        if (stage.nome !== funilFiltro) return prev.filter(t => t.id !== ticket.id);
        return prev.map(t => t.id === ticket.id ? { ...t, tag_gestao_id: tagObj.id, tag_gestao_nome: stage.nome } : t);
      });
      if (selectedTicket?.id === ticket.id) setClienteVinculado(prev => prev ? { ...prev, tag_gestao_id: tagObj.id, tag_gestao_nome: stage.nome } : prev);
      refreshFunilCounts();
      toast.success(`Funil: ${stage.nome}`);
    } catch (e) {
      toast.error("Erro ao mudar funil: " + (e.response?.data?.detail || e.message));
    }
  };

  // Conclui somente a etapa comercial. O ticket continua aberto e o historico
  // da conversa permanece disponivel para o atendimento.
  const finalizarProcesso = async (ticket) => {
    const stage = FUNIL_STAGES.find(s => s.nome === "Processo Finalizado");
    if (!stage) { toast.error("Etapa Simulação Concluída não configurada"); return; }
    await alterarTagGestaoTicket(ticket, stage);
  };

  // Qualquer alteracao visual de um ticket precisa atingir todas as fontes que
  // podem alimentar a lista lateral, alem do atendimento atualmente aberto.
  const atualizarTicketLocal = (id, atualizar) => {
    const aplicar = (lista) => lista.map(t => t.id === id ? atualizar(t) : t);
    setTickets(aplicar);
    setFunilTickets(aplicar);
    setVendaTickets(aplicar);
    setSelectedTicket(prev => prev?.id === id ? atualizar(prev) : prev);
  };

  const mudarStatusTicket = async (idsRecebidos, novoStatus) => {
    const ids = [...new Set((Array.isArray(idsRecebidos) ? idsRecebidos : [idsRecebidos]).filter(Boolean))];
    try {
      await Promise.all(ids.map(id => axios.post(`${API}/wpp/tickets/${id}/status`, { status: novoStatus })));
      ids.forEach(id => atualizarTicketLocal(id, t => ({ ...t, status: novoStatus, entrada_pendente: false, entrada_desde: null, nao_lidas: 0 })));
      if (selectedTicket?.id && ids.includes(selectedTicket.id)) {
        if (novoStatus === chatTab) setSelectedTicket(p => ({ ...p, status: novoStatus }));
        else { setSelectedTicket(null); setMensagens([]); }
      }
      carregarTudo(); toast.success(ids.length > 1 ? "Cliente movido com todas as conversas!" : "Status atualizado!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const aceitarTicket = async (id) => {
    const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
    try {
      await axios.put(`${API}/wpp/tickets/${id}`, { status: "EM_ATENDIMENTO", atendente_nome: usuario.nome || "Atendente" });
      carregarTudo(); toast.success("Aceito!");
    } catch { toast.error("Erro"); }
  };

  const excluirTicket = async (id) => {
    try { const r = await axios.delete(`${API}/wpp/tickets/${id}`);
      if (selectedTicket?.id === id) { setSelectedTicket(null); setMensagens([]); }
      carregarTudo();
      toast.success(r.data?.archived
        ? "Conversa arquivada. Uma nova mensagem reabre o atendimento."
        : "Excluido!");
    } catch { toast.error("Erro"); }
  };

  const transferirTicket = async (dados) => {
    if (!selectedTicket) return;
    try { await axios.post(`${API}/wpp/tickets/${selectedTicket.id}/transferir`, dados);
      setShowTransferir(false); carregarTudo(); toast.success("Transferido!");
    } catch { toast.error("Erro"); }
  };

  const addTagTicket = async (ticketId, tagNome) => {
    try {
      await axios.post(`${API}/wpp/tickets/${ticketId}/tags`, { tag: tagNome });
      const nome = String(tagNome || "").trim().toUpperCase();
      atualizarTicketLocal(ticketId, t => ({
        ...t,
        tags: [...new Set([...(t.tags || []), nome])],
        tags_info: { ...(t.tags_info || {}), [nome]: { added_at: new Date().toISOString() } },
      }));
      carregarTudo();
      toast.success("Tag adicionada!");
    }
    catch { toast.error("Erro"); }
  };
  const removeTagTicket = async (ticketId, tagNome) => {
    try {
      await axios.delete(`${API}/wpp/tickets/${ticketId}/tags/${tagNome}`);
      const nome = String(tagNome || "").trim().toUpperCase();
      atualizarTicketLocal(ticketId, t => {
        const tagsInfo = { ...(t.tags_info || {}) };
        delete tagsInfo[nome];
        return {
          ...t,
          tags: (t.tags || []).filter(tag => String(tag).trim().toUpperCase() !== nome),
          tags_info: tagsInfo,
        };
      });
      carregarTudo();
      toast.success("Tag removida!");
    }
    catch { toast.error("Erro"); }
  };

  const criarTag = async () => {
    try {
      const r = await axios.post(`${API}/wpp/tags`, novaTagForm);
      setTags(prev => [...prev.filter(t => t.id !== r.data.id), r.data].sort((a, b) => a.nome.localeCompare(b.nome)));
      setShowNovaTag(false); setNovaTagForm({ nome: "", cor: "#8b5cf6", categoria_id: "" });
      carregarTags().catch(e => console.error("Erro ao atualizar tags:", e));
      toast.success("Tag criada!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const editarTag = async () => {
    try {
      const r = await axios.put(`${API}/wpp/tags/${showEditTag.id}`, showEditTag);
      setTags(prev => prev.map(t => t.id === r.data.id ? r.data : t).sort((a, b) => a.nome.localeCompare(b.nome)));
      setShowEditTag(null);
      carregarTags().catch(e => console.error("Erro ao atualizar tags:", e));
      toast.success("Tag editada!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const excluirTag = async (id) => {
    try {
      await axios.delete(`${API}/wpp/tags/${id}`);
      setTags(prev => prev.filter(t => t.id !== id));
      carregarTags().catch(e => console.error("Erro ao atualizar tags:", e));
      toast.success("Tag excluida!");
    }
    catch { toast.error("Erro"); }
  };

  // Categorias de Tags
  const criarCategoria = async () => {
    try { await axios.post(`${API}/wpp/tag-categorias`, novaCategoriaForm);
      setNovaCategoriaForm({ nome: "", cor: "#6366f1" }); carregarTudo(); toast.success("Categoria criada!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const salvarEditCategoria = async () => {
    try { await axios.put(`${API}/wpp/tag-categorias/${editCategoria.id}`, editCategoria);
      setEditCategoria(null); carregarTudo(); toast.success("Categoria atualizada!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const excluirCategoria = async (id) => {
    if (!window.confirm("Excluir categoria? As tags ficarao sem categoria.")) return;
    try { await axios.delete(`${API}/wpp/tag-categorias/${id}`); carregarTudo(); toast.success("Categoria excluida"); }
    catch { toast.error("Erro"); }
  };
  const seedCategorias = async () => {
    try { const r = await axios.post(`${API}/wpp/tag-categorias/seed`);
      carregarTudo(); toast.success(`Seed OK: ${r.data.created} categorias criadas`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const moverTagParaCategoria = async (tagId, categoriaId) => {
    try { await axios.put(`${API}/wpp/tags/${tagId}`, { categoria_id: categoriaId || null });
      carregarTudo();
    } catch { toast.error("Erro ao mover tag"); }
  };

  const criarFila = async () => {
    try { await axios.post(`${API}/wpp/filas`, novaFilaForm);
      setShowNovaFila(false); setNovaFilaForm({ nome: "", cor: "#3b82f6", descricao: "" }); carregarTudo(); toast.success("Fila criada!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const excluirFila = async (id) => { try { await axios.delete(`${API}/wpp/filas/${id}`); carregarTudo(); toast.success("Excluida!"); } catch { toast.error("Erro"); } };
  const criarResposta = async () => {
    try { await axios.post(`${API}/wpp/respostas-rapidas`, novaRespostaForm);
      setShowNovaResposta(false); setNovaRespostaForm({ atalho: "", mensagem: "", categoria: "geral" }); carregarTudo(); toast.success("Criada!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const excluirResposta = async (id) => { try { await axios.delete(`${API}/wpp/respostas-rapidas/${id}`); carregarTudo(); toast.success("Excluida!"); } catch { toast.error("Erro"); } };

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/wpp/funil-counts`).then(r => { if (alive) setFunilCounts(r.data?.counts || {}); }).catch(() => {});
    axios.get(`${API}/wpp/venda-counts`).then(r => { if (alive) setVendaCounts(r.data?.counts || {}); }).catch(() => {});
    return () => { alive = false; };
  }, [tickets.length]);

  useEffect(() => {
    if (!vendaFiltro) { setVendaTickets([]); return; }
    let alive = true;
    axios.get(`${API}/wpp/tickets?venda=${encodeURIComponent(vendaFiltro)}&limit=500`)
      .then(r => { if (alive) setVendaTickets(r.data || []); })
      .catch(() => { if (alive) setVendaTickets([]); });
    return () => { alive = false; };
  }, [vendaFiltro]);

  const ticketsFiltrados = tickets
    .filter(t => filtroFila === "todos" || t.fila_id === filtroFila)
    .filter(t => filtroTag === "todos" || (t.tags || []).includes(filtroTag))
    .filter(t => !funilFiltro || t.tag_gestao_nome === funilFiltro)
    .filter(t => !busca || (t.contato_nome || "").toLowerCase().includes(busca.toLowerCase()) || (t.contato_telefone || "").includes(busca));

  // Quando uma etapa de funil esta selecionada, a lista exibida vem direto do fetch por etapa (fonte de verdade),
  // apenas aplicando o filtro de busca por texto.
  const buscaAtiva = busca.trim().length > 0;
  const listaChatBase = buscaAtiva
    ? buscaTickets
    : vendaFiltro
    ? vendaTickets.filter(t => !busca || (t.contato_nome || "").toLowerCase().includes(busca.toLowerCase()) || (t.contato_telefone || "").includes(busca))
    : funilFiltro
    ? funilTickets.filter(t => !busca || (t.contato_nome || "").toLowerCase().includes(busca.toLowerCase()) || (t.contato_telefone || "").includes(busca))
    : ticketsFiltrados;
  // Map tab key -> session_id Baileys (multi-segmento por chip)
  const PABX_TAB_TO_SESSION = {
    PABX_INSS: "pabx_inss",
    PABX_FEDERAL: "pabx_federal",
    PABX_MUNICIPAL: "pabx_municipal",
    PABX_ESTADUAL: "pabx_estadual",
    PABX_CLT: "pabx_clt",
    PABX_EXERCITO: "pabx_exercito",
    PABX_AERONAUTICA: "pabx_aeronautica",
    PABX_MARINHA: "pabx_marinha",
  };
  // PABX FEDERAL agrupa pabx_federal + pabx_federal__01/__02/__03 (multi-chip anti-ban)
  const matchPabxSession = (sid, tabKey) => {
    const base = PABX_TAB_TO_SESSION[tabKey];
    if (!base) return false;
    if (tabKey === "PABX_FEDERAL") return String(sid || "").startsWith("pabx_federal");
    return sid === base;
  };

  const ticketMatchesChannel = (ticket) => {
    if (canalFiltro === "TODOS") return true;
    if (canalFiltro === "WEB") return ticket.origem === "CHAT_WEB";
    if (canalFiltro === "PABX_URA") {
      if (ticket.origem !== "PABX") return false;
      return !pabxSegmentoFiltro || matchPabxSession(ticket.session_id, pabxSegmentoFiltro);
    }
    if (canalFiltro === "WHATSAPP") return ticket.origem !== "PABX" && ticket.origem !== "CHAT_WEB";
    return true;
  };

  const ticketMatchesStatus = (ticket) => {
    if (chatTab === "TODOS_GERAL") return true;
    if (chatTab === "ABERTOS_TODOS") return ticket.status !== "FINALIZADO";
    if (chatTab === "ENTRADA") return ticketEstaEmNovasMensagens(ticket);
    if (chatTab === "FLUXO_ROBO") return ticketPertenceAoStatusOperacional(ticket, "FLUXO_ROBO");
    if (chatTab === "URA_SOLICITACOES") return false;
    return ticketPertenceAoStatusOperacional(ticket, chatTab);
  };

  const listaChatFiltrada = buscaAtiva
    ? listaChatBase
    : funilFiltro
    ? listaChatBase.filter(ticketMatchesChannel)
    : listaChatBase.filter(ticketMatchesChannel).filter(ticketMatchesStatus);
  const listaChat = ordenarPorChegada(
    agruparAtendimentosCliente(listaChatFiltrada),
    !buscaAtiva && chatTab === "ENTRADA" && !funilFiltro && !vendaFiltro
  );
  const FUNIL_STAGES = [
    { nome: "Qualificação", bg: "#525252", bgInactive: "#F5F5F5", textInactive: "#525252", counterInactive: "#E5E5E5" },
    { nome: "Em Simulação Novo", bg: "#D97706", bgInactive: "#FEF3C7", textInactive: "#B45309", counterInactive: "#FDE68A" },
    { nome: "Em Simulação Refin & Saque", bg: "#C2410C", bgInactive: "#FFEDD5", textInactive: "#C2410C", counterInactive: "#FDBA74" },
    { nome: "Em Simulação Port", bg: "#B45309", bgInactive: "#FFEDD5", textInactive: "#9A3412", counterInactive: "#FDBA74" },
    { nome: "Depósito Sem Cadastro", bg: "#15803D", bgInactive: "#DCFCE7", textInactive: "#166534", counterInactive: "#BBF7D0" },
    { nome: "Processo Finalizado", label: "Simulação Concluída", bg: "#0F766E", bgInactive: "#CCFBF1", textInactive: "#115E59", counterInactive: "#99F6E4" },
  ];
  const chatTabs = [
    { key: "ENTRADA", label: "Novas mensagens", color: "#16a34a" },
    { key: "ABERTOS_TODOS", label: "Todos abertos", color: "#10b981" },
    { key: "ABERTO", label: "Aguardando", color: "#eab308" },
    { key: "SIMULACAO_BANCARIA", label: "Simulação Bancária", color: "#0284c7" },
    { key: "EM_ATENDIMENTO", label: "Em Atendimento", color: "#2563eb" },
    { key: "NEGOCIACAO", label: "Negociacao", color: "#16a34a" },
    { key: "ANALISE_BANCO", label: "Proposta em Análise", color: "#0ea5e9" },
    { key: "PABX", label: "Pabx (Todos)", color: "#0891b2" },
    { key: "PABX_INSS", label: "PABX INSS", color: "#06b6d4" },
    { key: "PABX_FEDERAL", label: "PABX FEDERAL", color: "#0ea5e9" },
    { key: "PABX_MUNICIPAL", label: "PABX MUNICIPAL", color: "#3b82f6" },
    { key: "PABX_ESTADUAL", label: "PABX ESTADUAL", color: "#6366f1" },
    { key: "PABX_CLT", label: "PABX CLT", color: "#a855f7" },
    { key: "PABX_EXERCITO", label: "PABX EXÉRCITO", color: "#22c55e" },
    { key: "PABX_AERONAUTICA", label: "PABX AERONÁUTICA", color: "#0d9488" },
    { key: "PABX_MARINHA", label: "PABX MARINHA", color: "#1d4ed8" },
    { key: "FLUXO_ROBO", label: "Fluxo Robo", color: "#f59e0b" },
    { key: "AGENDADO_MSG", label: "Agendados Msg", color: "#8b5cf6" },
    { key: "ATENDIMENTO_IA", label: "Atend. IA", color: "#059669" },
    { key: "PAUSADO_ROBO", label: "Pausado Robo", color: "#ea580c" },
    { key: "FINALIZADO", label: "Atendimento finalizado", color: "#6b7280" },
    { key: "TODOS_GERAL", label: "Todos Geral", color: "#7c3aed" },
    { key: "CHAT_WEB", label: "Visitante Web", color: "#14b8a6" },
    { key: "URA_SOLICITACOES", label: "Solicitações URA", color: "#db2777" },
  ];

  const VENDA_TABS = [
    { key: "VENDA_FALTA_DOCUMENTACAO", label: "Falta Documentação", color: "#f97316" },
    { key: "VENDA_FALTA_DIGITAR", label: "Falta Digitar", color: "#eab308" },
    { key: "VENDA_FALTA_FORMALIZAR", label: "Falta Formalizar", color: "#f59e0b" },
    { key: "VENDA_PENDENCIA_BANCO", label: "Pendência Banco", color: "#6366f1" },
    { key: "VENDA_CIP_SALDO", label: "CIP Saldo", color: "#8b5cf6" },
    { key: "VENDA_CIP_ACEITE", label: "CIP Aceite", color: "#4f46e5" },
  ];
  // Map dashboard server-side counts to tab keys (real totals, not limited by fetch)
  const dashboardCounts = {
    ENTRADA: dashboard.entrada ?? tickets.filter(t => t.status !== "FINALIZADO" && (t.entrada_pendente === true || (t.entrada_pendente == null && (t.nao_lidas || 0) > 0))).length,
    ABERTOS_TODOS: (dashboard.total_tickets || 0) - (dashboard.finalizado || 0),
    ABERTO: dashboard.abertos || 0,
    EM_ATENDIMENTO: dashboard.em_atendimento || 0,
    NEGOCIACAO: dashboard.negociacao || 0,
    AJUSTE: dashboard.ajuste || 0,
    PABX: dashboard.pabx || 0,
    PABX_INSS: dashboard.pabx_por_session?.pabx_inss || 0,
    PABX_FEDERAL: Object.entries(dashboard.pabx_por_session || {}).reduce((s, [k, v]) => (String(k).startsWith("pabx_federal") ? s + (v || 0) : s), 0),
    PABX_MUNICIPAL: dashboard.pabx_por_session?.pabx_municipal || 0,
    PABX_ESTADUAL: dashboard.pabx_por_session?.pabx_estadual || 0,
    PABX_CLT: dashboard.pabx_por_session?.pabx_clt || 0,
    PABX_EXERCITO: dashboard.pabx_por_session?.pabx_exercito || 0,
    PABX_AERONAUTICA: dashboard.pabx_por_session?.pabx_aeronautica || 0,
    PABX_MARINHA: dashboard.pabx_por_session?.pabx_marinha || 0,
    FLUXO_ROBO: dashboard.fluxo_robo || 0,
    AGENDADO_MSG: dashboard.agendado_msg || 0,
    ATENDIMENTO_IA: dashboard.atendimento_ia || 0,
    PAUSADO_ROBO: dashboard.pausado_robo || 0,
    FINALIZADO: dashboard.finalizado || 0,
    SIMULACAO_BANCARIA: dashboard.simulacao_bancaria || 0,
    ANALISE_BANCO: dashboard.analise_banco || 0,
    TODOS_GERAL: dashboard.total_tickets || 0,
    CHAT_WEB: dashboard.chat_web || 0,
    URA_SOLICITACOES: uraSolicCount,
  };

  const isAdminFunil = ["MASTER_GERAL", "ADMIN_EMPRESA"].includes(
    (JSON.parse(localStorage.getItem("usuario") || "{}")).perfil
  );
  const LINHA1_KEYS = ["TODOS_GERAL", "ABERTOS_TODOS", "FINALIZADO", "FLUXO_ROBO", "ATENDIMENTO_IA", "PAUSADO_ROBO", "AGENDADO_MSG"];
  const LINHA2_KEYS = ["ENTRADA", "ABERTO", "SIMULACAO_BANCARIA", "EM_ATENDIMENTO", "NEGOCIACAO", "ANALISE_BANCO"];
  const PABX_KEYS = ["URA_SOLICITACOES", "PABX_INSS", "PABX_FEDERAL", "PABX_MUNICIPAL", "PABX_ESTADUAL", "PABX_CLT", "PABX_EXERCITO", "PABX_AERONAUTICA", "PABX_MARINHA"];
  const renderStatusPill = (ct) => {
    const canalTickets = tickets.filter(ticketMatchesChannel);
    const tabTickets = ct.key === "TODOS_GERAL"
      ? canalTickets
      : ct.key === "ENTRADA"
      ? canalTickets.filter(ticketEstaEmNovasMensagens)
      : ct.key === "ABERTOS_TODOS"
      ? canalTickets.filter(t => t.status !== "FINALIZADO")
      : ct.key === "FLUXO_ROBO"
      ? canalTickets.filter(t => ticketPertenceAoStatusOperacional(t, "FLUXO_ROBO"))
      : canalTickets.filter(t => ticketPertenceAoStatusOperacional(t, ct.key));
    const count = canalFiltro === "TODOS"
      ? (dashboardCounts[ct.key] ?? tabTickets.length)
      : tabTickets.length;
    const unread = tabTickets.filter(t => (t.nao_lidas || 0) > 0).length;
    const isActive = chatTab === ct.key;
    return (
      <button key={ct.key} onClick={() => { if (ct.key !== chatTab) { setSelectedTicket(null); setMensagens([]); setFunilFiltro(null); setVendaFiltro(null); } if (ct.key === "ABERTOS_TODOS") setCanalFiltro("TODOS"); setChatTab(ct.key); }} aria-selected={isActive}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border cursor-pointer transition-colors whitespace-nowrap active:scale-95 ${isActive ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50"}`}
        data-testid={`status-filter-${ct.key}`}>
        <span className="leading-none">{ct.label}</span>
        <span className={`inline-flex items-center justify-center h-5 px-1.5 text-xs font-mono rounded-md ${isActive ? "bg-white/20 text-white" : "bg-neutral-100 text-neutral-500"}`}>{count}</span>
        {ct.key !== "ENTRADA" && unread > 0 && <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold rounded-full ${ct.key === "TODOS_GERAL" ? (isActive ? "bg-white/20 text-white" : "bg-neutral-100 text-neutral-500") : "bg-red-500 text-white"}`}>{unread}</span>}
      </button>
    );
  };

  const CANAIS_ENTRADA = [
    { key: "WHATSAPP", label: "WhatsApp", color: "#16A34A", count: dashboard.whatsapp || 0 },
    { key: "PABX_URA", label: "PABX / URA", color: "#0891B2", count: (dashboard.pabx || 0) + (uraSolicCount || 0) },
    { key: "WEB", label: "Web", color: "#0D9488", count: dashboard.chat_web || 0 },
  ];
  const selecionarCanalEntrada = (canal) => {
    if (canal.key !== canalFiltro) {
      setSelectedTicket(null);
      setMensagens([]);
    }
    setCanalFiltro(canal.key);
    if (canal.key !== "PABX_URA") setPabxSegmentoFiltro(null);
  };

  const renderVendaPill = (ct) => {
    const isActive = vendaFiltro === ct.key;
    const count = vendaCounts[ct.key] || 0;
    return (
      <button key={ct.key} onClick={() => { setSelectedTicket(null); setMensagens([]); setFunilFiltro(null); setVendaFiltro(isActive ? null : ct.key); }} aria-selected={isActive}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap active:scale-95 border"
        style={isActive ? { backgroundColor: ct.color, color: "#fff", borderColor: ct.color } : { backgroundColor: "#fff", color: "#525252", borderColor: "#e5e5e5" }}
        data-testid={`venda-filter-${ct.key}`}>
        <span className="leading-none">{ct.label}</span>
        <span className="inline-flex items-center justify-center h-5 px-1.5 text-xs font-mono rounded-md" style={isActive ? { backgroundColor: "rgba(255,255,255,0.2)", color: "#fff" } : { backgroundColor: "#f5f5f5", color: "#737373" }}>{count}</span>
      </button>
    );
  };

  const tempoNaTag = (isoDate) => {
    if (!isoDate) return "";
    const diff = Date.now() - new Date(isoDate).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const fmtRecTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col min-w-0 overflow-hidden" data-testid="atendimento-whatsapp">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b shrink-0">
        <div className="flex items-center gap-3">
          <MessageCircle className="h-5 w-5 text-green-600" />
          <h1 className="text-lg font-bold text-gray-800" data-testid="wpp-title">WhatsApp</h1>
          <Badge className={`text-[10px] gap-1 border ${baileysStatus.status === "connected" ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            {baileysStatus.status === "connected" ? <><Check className="h-3 w-3" /> Conectado {baileysStatus.number ? `(${baileysStatus.number.split("@")[0]})` : ""}</> : <><WifiOff className="h-3 w-3" /> Desconectado</>}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          {[
            { val: "kanban", Icon: Columns, label: "Kanban" },
            { val: "agendados", Icon: CalendarClock, label: "Agendados" },
            { val: "chat", Icon: MessageCircle, label: "Chat" },
            { val: "flows", Icon: GitBranch, label: "Flows" },
            { val: "tags", Icon: Tag, label: "Tags" },
            { val: "filas", Icon: Users, label: "Filas" },
            { val: "respostas", Icon: Zap, label: "Respostas" },
            { val: "dashboard", Icon: BarChart3, label: "Dashboard" },
          ].map(t => (
            <Button key={t.val} size="sm" variant={tab === t.val ? "default" : "ghost"}
              className={`h-7 text-xs gap-1 ${tab === t.val ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
              onClick={() => setTab(t.val)} data-testid={`tab-wpp-${t.val}`}>
              <t.Icon className="h-3 w-3" /> {t.label}
            </Button>
          ))}
        </div>
      </div>

      {/* === TAB: KANBAN === */}
      {tab === "kanban" && (
        <div className="flex-1 flex flex-col overflow-hidden bg-gray-100">
          <div className="flex items-center gap-2 px-4 py-2 bg-white border-b">
            <div className="relative flex-1 max-w-xs"><Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" /><Input className="pl-7 h-8 text-xs bg-gray-50" placeholder="Buscar cliente..." value={busca} onChange={e => setBusca(e.target.value)} /></div>
            <Badge className="bg-gray-100 text-gray-600 text-[10px]">{kanbanSelectedTags.length}/{tags.length} tags</Badge>
            {/* Kanban Tag Filter */}
            <div className="relative">
              <Button size="sm" variant={kanbanSelectedTags.length > 0 ? "default" : "outline"} className="h-7 text-[10px] gap-1 px-2" onClick={() => setShowKanbanFilter(!showKanbanFilter)} data-testid="kanban-filter-btn">
                <ListFilter className="h-3 w-3" /> Selecionar Tags {kanbanSelectedTags.length > 0 && `(${kanbanSelectedTags.length})`}
              </Button>
              {showKanbanFilter && (
                <div className="absolute right-0 top-8 z-50 bg-white border rounded-lg shadow-xl w-72 max-h-96 overflow-hidden" data-testid="kanban-filter-dropdown">
                  <div className="p-2 border-b bg-gray-50 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-500">Selecione as tags para o Kanban</span>
                    <div className="flex gap-1">
                      <button className="text-[9px] text-blue-600 hover:underline font-bold" onClick={() => setKanbanSelectedTags(tags.map(t => t.nome))}>Todas</button>
                      <span className="text-gray-300">|</span>
                      <button className="text-[9px] text-red-600 hover:underline font-bold" onClick={() => setKanbanSelectedTags([])}>Limpar</button>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-72 p-1">
                    {tags.map(tag => (
                      <label key={tag.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer text-xs">
                        <input type="checkbox" className="rounded border-gray-300 text-blue-600" checked={kanbanSelectedTags.includes(tag.nome)} onChange={e => {
                          if (e.target.checked) setKanbanSelectedTags(prev => [...prev, tag.nome]);
                          else setKanbanSelectedTags(prev => prev.filter(n => n !== tag.nome));
                        }} />
                        <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: tag.cor }} />
                        <span className="truncate">{tag.nome}</span>
                      </label>
                    ))}
                  </div>
                  <div className="p-2 border-t bg-gray-50">
                    <Button size="sm" className="w-full h-7 text-[10px]" onClick={() => setShowKanbanFilter(false)}>Aplicar</Button>
                  </div>
                </div>
              )}
            </div>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={carregarTudo}><RefreshCw className="h-3.5 w-3.5 text-gray-400" /></Button>
          </div>
          <div className="flex-1 flex gap-3 p-3 overflow-x-auto">
            {kanbanSelectedTags.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <ListFilter className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-semibold">Selecione as tags para exibir</p>
                  <p className="text-xs mt-1 mb-3">Use o botao "Selecionar Tags" acima para escolher quais colunas mostrar</p>
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowKanbanFilter(true)}>Selecionar Tags</Button>
                </div>
              </div>
            ) : tags.filter(t => kanbanSelectedTags.includes(t.nome)).map(tag => {
              const tagTickets = ticketsFiltrados.filter(tk => (tk.tags || []).includes(tag.nome));
              return (
                <div key={tag.id} className="min-w-[280px] max-w-[300px] flex flex-col rounded-xl bg-white shadow-sm border shrink-0" data-testid={`kanban-col-${tag.nome}`}>
                  <div className="flex items-center justify-between px-3 py-2.5 rounded-t-xl" style={{ backgroundColor: tag.cor + "15", borderBottom: `2px solid ${tag.cor}` }}>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: tag.cor }} />
                      <span className="font-bold text-sm" style={{ color: tag.cor }}>{tag.nome}</span>
                    </div>
                    <Badge className="text-[10px] font-bold text-white" style={{ backgroundColor: tag.cor }}>{tagTickets.length}</Badge>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                    {tagTickets.length === 0 ? (
                      <div className="text-center py-6 text-gray-300"><Users className="h-6 w-6 mx-auto mb-1 opacity-40" /><p className="text-[10px]">Nenhum cliente</p></div>
                    ) : tagTickets.map(tk => {
                      const tagInfo = tk.tags_info?.[tag.nome];
                      const addedAt = tagInfo?.added_at;
                      return (
                        <div key={tk.id} className="border rounded-lg p-2.5 hover:shadow-sm transition-shadow bg-white cursor-pointer" onClick={() => { setSelectedTicket(tk); setTab("chat"); }}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <Avatar nome={tk.contato_nome} />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs block truncate">{tk.contato_nome || tk.contato_telefone}</span>
                              <span className="text-[9px] text-gray-400">{tk.is_lid && isLidNumber(tk.contato_telefone) ? <span className="text-amber-500 font-bold">LID</span> : tk.contato_telefone}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-[9px] text-gray-400">
                            <div className="flex items-center gap-1"><Clock className="h-2.5 w-2.5" /><span>{addedAt ? `ha ${tempoNaTag(addedAt)}` : "sem data"}</span></div>
                            <span>{addedAt ? new Date(addedAt).toLocaleDateString("pt-BR") : ""}</span>
                          </div>
                          <div className="mt-1.5 flex items-center justify-between">
                            <div className="flex gap-0.5 flex-wrap">
                              {(tk.tags || []).filter(t => t !== tag.nome).slice(0, 2).map(t => {
                                const tObj = tags.find(tg => tg.nome === t);
                                return <span key={t} className="text-[7px] font-bold px-1 py-0.5 rounded text-white" style={{ backgroundColor: tObj?.cor || "#8b5cf6" }}>{t}</span>;
                              })}
                            </div>
                            <button className="text-[9px] font-medium px-2 py-0.5 rounded text-blue-600 bg-blue-50 hover:bg-blue-100" onClick={e => { e.stopPropagation(); setSelectedTicket(tk); setTab("chat"); }}>Ver Ticket</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* === TAB: AGENDADOS === */}
      {tab === "agendados" && (
        <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
          {/* Header with filters */}
          <div className="flex items-center justify-between px-4 py-3 bg-white border-b">
            <div className="flex items-center gap-3">
              <CalendarClock className="h-5 w-5 text-purple-600" />
              <h2 className="text-base font-bold text-gray-800">Agendamentos</h2>
              <Badge className="bg-purple-100 text-purple-700 text-[10px]">{agendamentos.filter(a => a.status === "AGENDADA").length} pendentes</Badge>
            </div>
            <div className="flex items-center gap-2">
              {/* Period filter */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                {[{ key: "todos", label: "Todos" }, { key: "dia", label: "Dia" }, { key: "mes", label: "Mes" }, { key: "ano", label: "Ano" }].map(f => (
                  <button key={f.key} onClick={() => setAgendamentosFiltro(f.key)}
                    className={`text-[10px] px-2.5 py-1 rounded-md font-medium transition-colors ${agendamentosFiltro === f.key ? "bg-white text-purple-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    data-testid={`filtro-agendamento-${f.key}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              {agendamentosFiltro !== "todos" && (
                <Input type="date" value={agendamentosData} onChange={e => setAgendamentosData(e.target.value)}
                  className="h-8 text-xs w-36" data-testid="input-data-agendamento" />
              )}
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={carregarTudo}><RefreshCw className="h-3.5 w-3.5 text-gray-400" /></Button>
            </div>
          </div>
          {/* Status filter + Search + Batch resend */}
          <div className="flex items-center gap-2 px-4 py-2 bg-white border-b flex-wrap">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              {[{ key: "todos", label: "Todos" }, { key: "AGENDADA", label: "Pendentes" }, { key: "ENVIADA", label: "Enviados" }, { key: "ERRO", label: "Erros" }].map(f => (
                <button key={f.key} onClick={() => setAgendamentosStatus(f.key)}
                  className={`text-[11px] px-3 py-1 rounded-md font-medium transition-colors ${agendamentosStatus === f.key ? (f.key === "ERRO" ? "bg-red-500 text-white shadow-sm" : "bg-white text-purple-700 shadow-sm") : "text-gray-500 hover:text-gray-700"}`}
                  data-testid={`filtro-status-agendamento-${f.key}`}>
                  {f.label}
                  {f.key === "ERRO" && agendamentos.filter(a => a.status === "ERRO").length > 0 && (
                    <span className="ml-1 opacity-80">({agendamentos.filter(a => a.status === "ERRO").length})</span>
                  )}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input value={agendamentosBusca} onChange={e => setAgendamentosBusca(e.target.value)}
                placeholder="Buscar CPF, nome ou telefone..." className="h-8 text-xs pl-7" data-testid="input-busca-agendamento" />
            </div>
            <Button size="sm" variant="outline" disabled={reenviandoLote} onClick={reenviarErrosLote}
              className="h-8 text-[11px] px-3 text-red-600 border-red-300 hover:bg-red-50" data-testid="btn-reenviar-erros-lote">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${reenviandoLote ? "animate-spin" : ""}`} /> Reenviar todos os Erros
            </Button>
          </div>
          {/* Agendamentos list */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="max-w-4xl mx-auto space-y-2">
              {(() => {
                let filtered = agendamentos;
                if (agendamentosFiltro === "dia") filtered = agendamentos.filter(a => a.data_agendamento?.slice(0, 10) === agendamentosData);
                else if (agendamentosFiltro === "mes") filtered = agendamentos.filter(a => a.data_agendamento?.slice(0, 7) === agendamentosData.slice(0, 7));
                else if (agendamentosFiltro === "ano") filtered = agendamentos.filter(a => a.data_agendamento?.slice(0, 4) === agendamentosData.slice(0, 4));
                if (agendamentosStatus !== "todos") filtered = filtered.filter(a => a.status === agendamentosStatus);
                if (agendamentosBusca.trim()) {
                  const q = agendamentosBusca.trim().toLowerCase();
                  const qd = q.replace(/\D/g, "");
                  filtered = filtered.filter(a =>
                    (a.contato_nome || "").toLowerCase().includes(q) ||
                    (a.contato_telefone || "").includes(qd || q) ||
                    (a.contato_cpf || "").includes(qd || q)
                  );
                }
                if (filtered.length === 0) return (
                  <div className="text-center py-16 text-gray-400">
                    <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-medium">Nenhum agendamento</p>
                    <p className="text-xs mt-1">Crie agendamentos no chat de um cliente</p>
                  </div>
                );
                return filtered.map(ag => (
                  <Card key={ag.id} className={`border-l-4 ${ag.status === "AGENDADA" ? "border-l-purple-500" : ag.status === "ENVIADA" ? "border-l-green-500" : "border-l-gray-400"}`} data-testid={`agendamento-card-${ag.id}`}>
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar nome={ag.contato_nome} />
                          <div>
                            <p className="font-semibold text-sm">{ag.contato_nome || ag.contato_telefone}</p>
                            <p className="text-[10px] text-gray-400">Atribuido a: {ag.atendente_nome || "-"}</p>
                            <div className="flex items-center gap-1.5 flex-wrap mt-1" data-testid={`agendamento-pilulas-${ag.id}`}>
                              {ag.status_ticket && <StatusBadge status={ag.status_ticket} chatTabs={chatTabs} flowActive={ag.flow_active} />}
                              {ag.tag_gestao_nome && (() => {
                                const cur = FUNIL_STAGES.find(st => st.nome === ag.tag_gestao_nome);
                                return (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border"
                                    style={cur ? { backgroundColor: cur.bgInactive, color: cur.textInactive, borderColor: cur.bgInactive } : { backgroundColor: "#F9FAFB", color: "#9CA3AF", borderColor: "#D1D5DB" }}
                                    data-testid={`agendamento-funil-${ag.id}`}>
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cur ? cur.bg : "#D1D5DB" }} />
                                    {etapaLabel(ag.tag_gestao_nome)}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
                              <Clock className="h-3 w-3 text-purple-500" />
                              {fmtAgendaFull(ag.status === "ENVIADA" ? (ag.updated_at || ag.data_agendamento) : ag.data_agendamento)}
                            </p>
                            <Badge className={`text-[9px] mt-0.5 ${ag.status === "AGENDADA" ? "bg-purple-100 text-purple-700" : ag.status === "ENVIADA" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                              {ag.status}
                            </Badge>
                          </div>
                          {ag.status === "AGENDADA" && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-green-600 border-green-300" onClick={() => enviarAgendamentoAgora(ag.id)} data-testid={`btn-enviar-agendamento-${ag.id}`}>Enviar agora</Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => cancelarAgendamento(ag.id)}><XCircle className="h-3.5 w-3.5" /></Button>
                            </div>
                          )}
                          {ag.status !== "AGENDADA" && (
                            <div className="flex gap-1">
                              {ag.status === "ERRO" && (
                                <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-blue-600 border-blue-300" onClick={() => enviarAgendamentoAgora(ag.id)} data-testid={`btn-reenviar-agendamento-${ag.id}`}>Reenviar</Button>
                              )}
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => excluirAgendamento(ag.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          )}
                        </div>
                      </div>
                      {ag.tipo === "mensagem" && ag.conteudo && (
                        <p className="text-xs text-gray-500 mt-2 bg-gray-50 rounded px-2 py-1 line-clamp-2">{ag.conteudo}</p>
                      )}
                      {ag.tipo === "fluxo" && (
                        <p className="text-xs text-purple-500 mt-2 flex items-center gap-1"><GitBranch className="h-3 w-3" /> Fluxo: {ag.flow_nome}</p>
                      )}
                      {ag.status === "ERRO" && (
                        <div className="mt-2 flex items-start gap-1.5 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid={`agendamento-erro-motivo-${ag.id}`}>
                          <AlertCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                          <p className="text-[11px] text-red-600 font-medium">{ag.erro_motivo || "Erro no envio (motivo nao registrado - clique em Reenviar)"}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* === TAB: CHAT === */}
      {tab === "chat" && (
        <div className="flex flex-col flex-1 overflow-hidden min-w-0">
          {/* === BARRAS HORIZONTAIS: Status Gerais + PABX + Web + Funil (admin) === */}
          <div className="flex flex-col px-4 pt-3 pb-2.5 bg-white border-b shrink-0 gap-2 min-w-0">
            {/* Linha 1: Geral */}
            <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="status-bar">
              {LINHA1_KEYS.map(k => chatTabs.find(ct => ct.key === k)).filter(Boolean).map(renderStatusPill)}
            </div>
            {/* Canal de entrada: uma unica linha substitui os tres blocos antigos. */}
            <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pt-1.5 border-t border-neutral-100" data-testid="canal-entrada-bar">
              <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 shrink-0 pr-1">Canal de entrada</span>
              {CANAIS_ENTRADA.map(canal => {
                const isActive = canalFiltro === canal.key;
                return (
                  <button key={canal.key} type="button" onClick={() => selecionarCanalEntrada(canal)} aria-selected={isActive}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border cursor-pointer transition-colors whitespace-nowrap active:scale-95"
                    style={isActive ? { backgroundColor: canal.color, color: "#fff", borderColor: canal.color } : { backgroundColor: "#fff", color: "#525252", borderColor: "#e5e5e5" }}
                    data-testid={`canal-entrada-${canal.key}`}>
                    <span className="leading-none">{canal.label}</span>
                    <span className="inline-flex items-center justify-center h-5 px-1.5 text-xs font-mono rounded-md" style={isActive ? { backgroundColor: "rgba(255,255,255,0.2)", color: "#fff" } : { backgroundColor: "#f5f5f5", color: "#737373" }}>{canal.count}</span>
                  </button>
                );
              })}
            </div>
            {/* Status permanece livre e combina com qualquer canal selecionado. */}
            <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="atendimento-status-bar">
              <span className="text-[10px] font-bold uppercase tracking-wider text-green-600 shrink-0 pr-1">Status do atendimento</span>
              {LINHA2_KEYS.map(k => chatTabs.find(ct => ct.key === k)).filter(Boolean).map(renderStatusPill)}
            </div>
            {canalFiltro === "PABX_URA" && (
              <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="pabx-segmentos-bar">
                <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-600 shrink-0 pr-1">Segmento</span>
                <button type="button" onClick={() => setPabxSegmentoFiltro(null)}
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border whitespace-nowrap ${!pabxSegmentoFiltro ? "bg-cyan-700 text-white border-cyan-700" : "bg-white text-neutral-600 border-neutral-200"}`}
                  data-testid="pabx-segmento-todos">Todos</button>
                {PABX_KEYS.filter(k => k.startsWith("PABX_")).map(k => chatTabs.find(ct => ct.key === k)).filter(Boolean).map(ct => {
                  const ativo = pabxSegmentoFiltro === ct.key;
                  const total = tickets.filter(t => t.origem === "PABX" && matchPabxSession(t.session_id, ct.key)).length;
                  return (
                    <button key={ct.key} type="button" onClick={() => setPabxSegmentoFiltro(ativo ? null : ct.key)}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border whitespace-nowrap ${ativo ? "bg-cyan-700 text-white border-cyan-700" : "bg-white text-neutral-600 border-neutral-200"}`}
                      data-testid={`pabx-segmento-${ct.key}`}>
                      <span>{ct.label.replace("PABX ", "")}</span><span className="text-xs opacity-70">{total}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {/* Etapa comercial: independente do status do atendimento. */}
            {isAdminFunil && (
              <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pt-1.5 border-t border-neutral-100" data-testid="funnel-bar">
                <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 shrink-0 pr-1">Etapa do processo</span>
                <button onClick={() => setFunilFiltro(null)} aria-selected={!funilFiltro}
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap active:scale-95 ${!funilFiltro ? "bg-neutral-700 text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}
                  data-testid="funnel-filter-todos">Todos</button>
                {FUNIL_STAGES.map(st => {
                  const isActive = funilFiltro === st.nome;
                  const count = funilCounts[st.nome] || 0;
                  return (
                    <button key={st.nome} onClick={() => { setVendaFiltro(null); setFunilFiltro(isActive ? null : st.nome); }} aria-selected={isActive}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap active:scale-95"
                      style={isActive ? { backgroundColor: st.bg, color: "#fff" } : { backgroundColor: st.bgInactive, color: st.textInactive }}
                      data-testid={`funnel-filter-${st.nome}`}>
                      <span className="leading-none">{st.label || st.nome}</span>
                      <span className="inline-flex items-center justify-center h-5 px-1.5 text-xs font-mono rounded-md" style={isActive ? { backgroundColor: "rgba(255,255,255,0.2)", color: "#fff" } : { backgroundColor: st.counterInactive }}>{count}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex flex-1 overflow-hidden min-w-0">
          {/* Sidebar */}
          <div className="w-[340px] border-r bg-white flex flex-col shrink-0">
            <div className="p-2 border-b bg-gray-50/50">
              <div className="flex gap-1">
                <div className="relative flex-1">
                  {buscaCarregando ? <Loader2 className="absolute left-2 top-2 h-3.5 w-3.5 text-green-600 animate-spin" /> : <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />}
                  <Input className="pl-7 h-8 text-xs bg-white" placeholder="Buscar nome, telefone ou CPF..." value={busca} onChange={e => setBusca(e.target.value)} data-testid="buscar-cliente-global" />
                </div>
                <Button size="sm" className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700" onClick={() => setShowNovoTicket(true)}><Plus className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" className="h-8 text-[10px] px-2 gap-1 text-sky-600 border-sky-300 hover:bg-sky-50" onClick={vincularLids} data-testid="btn-vincular-lids" title="Vincular telefone e LID de todos os contatos">Vincular LIDs</Button>
              </div>
              <div className="mt-1">
                <Input className="h-7 text-[10px] bg-white" placeholder="Buscar tag..." value={analiseBuscaTag} onChange={e => { setAnaliseBuscaTag(e.target.value); if (!e.target.value) setFiltroTag("todos"); }} data-testid="buscar-tag-filter" />
                {analiseBuscaTag && (
                  <div className="mt-1 max-h-[160px] overflow-y-auto space-y-0.5 bg-white border rounded p-1">
                    <button onClick={() => { setFiltroTag("todos"); setAnaliseBuscaTag(""); }}
                      className={`w-full flex items-center justify-between px-2 py-1 rounded text-[10px] transition-all ${filtroTag === "todos" ? "bg-sky-100 text-sky-800 font-bold" : "hover:bg-gray-100 text-gray-600"}`}>
                      <span>Todas as tags</span>
                      <span className="bg-gray-200 text-gray-600 rounded px-1.5 text-[9px]">{tags.length}</span>
                    </button>
                    {tags.filter(t => t.nome.toLowerCase().includes(analiseBuscaTag.toLowerCase())).map(t => (
                      <button key={t.id} onClick={() => { setFiltroTag(t.nome); setAnaliseBuscaTag(t.nome); }}
                        className={`w-full flex items-center justify-between px-2 py-1 rounded text-[10px] transition-all ${filtroTag === t.nome ? "bg-sky-100 text-sky-800 font-bold" : "hover:bg-gray-100 text-gray-600"}`}
                        data-testid={`tag-filter-${t.nome}`}>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: t.cor || "#8b5cf6" }} />
                          <span className="truncate">{t.nome}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {filtroTag !== "todos" && (
                  <div className="mt-1 flex items-center gap-1">
                    <span className="text-[9px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">Tag: {filtroTag}</span>
                    <button onClick={() => { setFiltroTag("todos"); setAnaliseBuscaTag(""); }} className="text-[9px] text-red-400 hover:text-red-600">✕</button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto relative">
              {chatTab === "URA_SOLICITACOES" && (
                <div className="absolute inset-0 bg-white z-10">
                  <UraSolicitacoes onContatoWhatsapp={contatarLeadWhatsapp} />
                </div>
              )}
              {listaChat.length === 0 ? (
                <div className="text-center py-12 text-gray-400" data-testid="empty-conversations"><MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-30" /><p className="text-xs">{buscaAtiva ? "Nenhum cliente encontrado" : "Nenhuma conversa"}</p></div>
              ) : listaChat.map(tk => (
                <div key={tk.id} data-testid={`ticket-item-${tk.id}`} className={`px-3 py-2 cursor-pointer border-b hover:bg-gray-50 transition-colors ${selectedTicket?.id === tk.id ? "bg-green-50 border-l-2 border-l-green-500" : ""} ${tk.nao_lidas > 0 && selectedTicket?.id !== tk.id ? "bg-green-50/50" : ""}`} onClick={() => setSelectedTicket(tk)}>
                  <div className="flex items-center gap-2">
                    <Avatar nome={tk.contato_nome} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="font-semibold text-xs truncate">{tk.contato_nome || tk.contato_telefone}</span>
                          {(tk.canais_atendimento || []).length > 1 && (
                            <span className="shrink-0 text-[8px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1 py-0.5"
                              title={(tk.canais_atendimento || []).map(c => CHANNEL_META[c]?.label || c).join(", ")}
                              data-testid="ticket-omnichannel-count">
                              {(tk.canais_atendimento || []).length} canais
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {tk.is_lid && isLidNumber(tk.contato_telefone) && <span className="text-[8px] font-bold text-amber-500">LID</span>}
                          {tk.nao_lidas > 0 && (
                            <span className="bg-green-500 text-white text-[9px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full">{tk.nao_lidas}</span>
                          )}
                          <span className="text-[9px] text-gray-400">{fmt(tk.updated_at)}</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-500 truncate">{tk.ultima_mensagem || "Nova conversa"}</p>
                    </div>
                  </div>
                  <StatusBadge status={tk.status} chatTabs={chatTabs} ticketId={tk.id} ticketIds={tk.tickets_atendimento || [tk.id]} onChangeStatus={mudarStatusTicket} flowActive={tk.flow_state?.active === true} permitirStatusAtual={chatTab === "ENTRADA"} emNovasMensagens={ticketEstaEmNovasMensagens(tk)} />
                  {/* Seletor de etapa do processo direto na lista lateral */}
                  {(() => {
                    const cur = FUNIL_STAGES.find(s => s.nome === tk.tag_gestao_nome);
                    return (
                      <Popover open={funilPickerFor === tk.id} onOpenChange={aberto => setFunilPickerFor(aberto ? tk.id : null)}>
                        <PopoverTrigger asChild>
                          <button
                            onClick={e => e.stopPropagation()}
                            className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border transition-colors"
                            style={cur ? { backgroundColor: cur.bgInactive, color: cur.textInactive, borderColor: cur.bgInactive } : { backgroundColor: "#F9FAFB", color: "#9CA3AF", borderStyle: "dashed", borderColor: "#D1D5DB" }}
                            data-testid={`card-funil-btn-${tk.id}`}
                            title="Alterar etapa do processo">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cur ? cur.bg : "#D1D5DB" }} />
                            <span className="truncate max-w-[120px]">{cur?.label || cur?.nome || "Definir etapa"}</span>
                            <ChevronDown className="h-2.5 w-2.5 opacity-60" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="top"
                          sideOffset={6}
                          collisionPadding={8}
                          align="start"
                          className="w-[190px] max-h-[min(16rem,var(--radix-popover-content-available-height))] overflow-y-auto p-1 z-[100]"
                          data-testid={`card-funil-menu-${tk.id}`}
                          onClick={e => e.stopPropagation()}
                        >
                          {FUNIL_STAGES.map(st => (
                            <button key={st.nome}
                              onClick={() => { alterarTagGestaoTicket(tk, st); setFunilPickerFor(null); }}
                              className="flex items-center gap-1.5 w-full px-2 py-1 rounded text-[10px] hover:bg-gray-100 text-left transition-colors"
                              data-testid={`card-funil-opt-${tk.id}-${st.nome}`}>
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: st.bg }} />
                              <span className="truncate">{st.label || st.nome}</span>
                              {tk.tag_gestao_nome === st.nome && <Check className="h-3 w-3 ml-auto text-green-600" />}
                            </button>
                          ))}
                        </PopoverContent>
                      </Popover>
                    );
                  })()}
                </div>
              ))}
              {chatTab && !funilFiltro && !["ENTRADA","TODOS_GERAL","ABERTOS_TODOS","CHAT_WEB","URA_SOLICITACOES","FLUXO_ROBO","PABX"].includes(chatTab) && !chatTab.startsWith("PABX_") && (dashboardCounts[chatTab] || 0) > tickets.filter(t => t.status === chatTab && t.origem !== "PABX" && t.origem !== "CHAT_WEB").length && (
                <div className="p-3 text-center">
                  <button data-testid="btn-carregar-mais-tickets" disabled={carregandoMais} onClick={carregarMaisTickets}
                    className="text-xs px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors">
                    {carregandoMais ? "Carregando..." : `Carregar mais (${(dashboardCounts[chatTab] || 0) - tickets.filter(t => t.status === chatTab && t.origem !== "PABX" && t.origem !== "CHAT_WEB").length} restantes)`}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 flex flex-col bg-[#efeae2]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z' fill='%23d5cec0' fill-opacity='0.12'/%3E%3C/svg%3E\")" }}>
            {!selectedTicket ? (
              <div className="flex-1 flex items-center justify-center"><div className="text-center"><div className="w-36 h-36 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center"><MessageCircle className="h-16 w-16 text-gray-300" /></div><h3 className="text-lg font-light text-gray-500">Selecione uma conversa</h3></div></div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="flex items-center justify-between px-4 py-2 bg-[#f0f2f5] border-b">
                  <div className="flex items-center">
                    <TicketTimer inicioSessao={selectedTicket.inicio_sessao || selectedTicket.created_at} status={selectedTicket.status} />
                  </div>
                  <div className="flex items-center gap-2">
                    {ticketAgendamentos.filter(a => a.status === "AGENDADA").length > 0 && (
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 border border-purple-200 rounded-lg" data-testid="header-agendamento-info">
                        <CalendarClock className="h-3.5 w-3.5 text-purple-600" />
                        <span className="text-[10px] font-bold text-purple-700">
                          {new Date(ticketAgendamentos.find(a => a.status === "AGENDADA").data_agendamento).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" className="h-7 px-2 text-[11px] gap-1 bg-violet-600 hover:bg-violet-700 text-white"
                      onClick={() => setShowProspecta(true)} data-testid="wpp-btn-simular-prospecta">
                      <Briefcase className="h-3.5 w-3.5" /> Simular CLT
                    </Button>
                    {(selectedTicket.entrada_pendente === true || (selectedTicket.entrada_pendente == null && (selectedTicket.nao_lidas || 0) > 0)) && (
                      <Button size="sm" className="h-7 text-xs gap-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={marcarAtendido} data-testid="btn-marcar-atendido" title="Retirar de Novas mensagens e mover para Aguardando">
                        <Check className="h-3 w-3" /> Atendido
                      </Button>
                    )}
                    {/* Agendar button */}
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-purple-600 border-purple-300 hover:bg-purple-50"
                      onClick={() => setShowCriarAgendamento(true)} data-testid="btn-agendar">
                      <CalendarClock className="h-3 w-3" /> Agendar
                    </Button>
                    {/* Pausar Robo button */}
                    <Button size="sm" variant={selectedTicket.status === "PAUSADO_ROBO" ? "default" : "outline"}
                      className={`h-7 text-xs gap-1 ${selectedTicket.status === "PAUSADO_ROBO" ? "bg-orange-500 hover:bg-orange-600 text-white" : "text-orange-500 border-orange-300 hover:bg-orange-50"}`}
                      onClick={togglePausarRobo} data-testid="btn-pausar-robo">
                      {selectedTicket.status === "PAUSADO_ROBO" ? <><PlayCircle className="h-3 w-3" /> Ativar Robo</> : <><PauseCircle className="h-3 w-3" /> Pausar Robo</>}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-teal-700 border-teal-300 hover:bg-teal-50 disabled:opacity-60"
                      onClick={() => finalizarProcesso(selectedTicket)} data-testid="btn-finalizar-processo"
                      disabled={selectedTicket.tag_gestao_nome === "Processo Finalizado"}
                      title="Conclui a simulação e mantém a proposta em andamento">
                      <Check className="h-3 w-3" /> {selectedTicket.tag_gestao_nome === "Processo Finalizado" ? "Simulação concluída" : "Concluir simulação"}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-green-600"
                      onClick={() => resolverTicket(selectedTicket.id)} data-testid="btn-resolver-ticket"
                      title="Encerra este atendimento e fecha a conversa">
                      <Check className="h-3 w-3" /> Finalizar atendimento
                    </Button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Mais opções" data-testid="btn-mais-opcoes">
                          <MoreHorizontal className="h-4 w-4 text-gray-500" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-52 p-1 z-[120]">
                        <button type="button" className="w-full flex items-center gap-2 px-2 py-2 text-xs rounded hover:bg-gray-50" onClick={() => setShowTransferir(true)}>
                          <ArrowRightLeft className="h-3.5 w-3.5" /> Transferir conversa
                        </button>
                        <button type="button" className="w-full flex items-center gap-2 px-2 py-2 text-xs rounded hover:bg-gray-50" onClick={() => window.open(`${API}/wpp/tickets/${selectedTicket.id}/exportar-pdf`, "_blank")} data-testid="btn-exportar-pdf">
                          <FileText className="h-3.5 w-3.5 text-blue-500" /> Exportar PDF
                        </button>
                        <button type="button" className="w-full flex items-center gap-2 px-2 py-2 text-xs rounded hover:bg-gray-50" onClick={async () => {
                          try {
                            await axios.post(`${API}/wpp/tickets/${selectedTicket.id}/salvar-conversa`);
                            toast.success("Conversa salva no cadastro do cliente!");
                          } catch { toast.error("Erro ao salvar ou cliente não vinculado"); }
                        }} data-testid="btn-salvar-conversa">
                          <FilePlus2 className="h-3.5 w-3.5 text-emerald-500" /> Salvar no cadastro
                        </button>
                        <Separator className="my-1" />
                        <button type="button" className="w-full flex items-center gap-2 px-2 py-2 text-xs text-red-600 rounded hover:bg-red-50" onClick={() => excluirTicket(selectedTicket.id)}>
                          <Trash2 className="h-3.5 w-3.5" /> Excluir conversa
                        </button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-2">
                  {mensagens.length === 0 && <div className="flex justify-center mt-6"><span className="text-[10px] text-gray-400 bg-white/80 px-3 py-1 rounded-full shadow-sm">Inicio da conversa</span></div>}
                  {mensagens.map(msg => <MessageBubble key={msg.id} msg={msg} onTranscribe={transcribeAudio} transcriptions={transcriptions} onReply={(alvo) => {
                    setReplyingTo(alvo);
                    if (alvo?.ticket_id) setReplyTargetTicketId(alvo.ticket_id);
                  }} />)}
                  <div ref={msgEndRef} />
                </div>

                {/* Chat Input Area with Enhancements */}
                <div className="bg-[#f0f2f5] px-3 py-2 border-t relative">
                  <div className="mb-1.5 flex items-center gap-2 text-[10px] text-gray-500" data-testid="reply-channel-indicator">
                    <span>Respondendo por</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="inline-flex items-center gap-1 rounded hover:bg-white px-1 py-0.5" title="Trocar canal da resposta" data-testid="reply-channel-selector">
                          <ChannelBadge channel={replyTarget?.canal} label={replyTarget?.canal_label} />
                          {replyTargetOptions.length > 1 && <ChevronDown className="h-3 w-3 text-gray-400" />}
                        </button>
                      </PopoverTrigger>
                      {replyTargetOptions.length > 1 && (
                        <PopoverContent align="start" className="w-56 p-1 z-[120]">
                          <p className="px-2 py-1 text-[10px] font-semibold text-gray-500">Responder por:</p>
                          {replyTargetOptions.map(op => (
                            <button key={op.ticket_id} type="button" onClick={() => setReplyTargetTicketId(op.ticket_id)}
                              className={`w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-gray-50 ${replyTicketId === op.ticket_id ? "bg-gray-50" : ""}`}
                              data-testid={`reply-channel-option-${op.canal}`}>
                              <ChannelBadge channel={op.canal} label={op.canal_label} />
                              {replyTicketId === op.ticket_id && <Check className="h-3 w-3 text-green-600" />}
                            </button>
                          ))}
                        </PopoverContent>
                      )}
                    </Popover>
                    {!replyTargetTicketId && ultimaEntradaCliente && (
                      <span className="text-[9px] text-gray-400">último canal usado pelo cliente</span>
                    )}
                  </div>
                  {/* Emoji Picker Popup */}
                  {showEmoji && (
                    <div ref={emojiRef} className="absolute bottom-14 left-3 z-50" data-testid="emoji-picker-container">
                      <EmojiPicker
                        onEmojiClick={(emojiData) => { setMsgInput(prev => prev + emojiData.emoji); }}
                        width={320} height={380}
                        searchPlaceholder="Buscar emoji..."
                        previewConfig={{ showPreview: false }}
                        skinTonesDisabled
                        lazyLoadEmojis
                      />
                    </div>
                  )}

                  {/* Flow Picker Popup */}
                  {showFlowPicker && (
                    <div className="absolute bottom-14 left-12 z-50 bg-white border shadow-xl rounded-lg p-3 min-w-[280px] max-h-[300px] overflow-y-auto" data-testid="flow-picker">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold text-gray-700">Enviar Fluxo</p>
                        <button onClick={() => setShowFlowPicker(false)}><X className="h-3.5 w-3.5 text-gray-400" /></button>
                      </div>
                      {flows.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-4">Nenhum fluxo criado. Crie na aba "Flows".</p>
                      ) : flows.map(f => (
                        <button key={f.id} className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-green-50 text-left transition-colors" onClick={() => sendFlow(f)} data-testid={`flow-option-${f.id}`}>
                          <GitBranch className="h-4 w-4 text-green-600 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-700 truncate">{f.nome}</p>
                            {f.descricao && <p className="text-[10px] text-gray-400 truncate">{f.descricao}</p>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Reply preview - mensagem sendo citada */}
                  {replyingTo && (
                    <div className="mb-2 flex items-stretch bg-gray-50 border-l-4 border-green-500 rounded overflow-hidden" data-testid="reply-preview">
                      <div className="flex-1 px-3 py-2 min-w-0">
                        <p className="text-[11px] font-semibold text-green-700">
                          Respondendo {replyingTo.direcao === "saida" ? "sua mensagem" : (replyingTo.remetente_nome || "cliente")}
                        </p>
                        <p className="text-xs text-gray-600 truncate italic">
                          {(replyingTo.corpo || "").trim() || `[${replyingTo.tipo || "mídia"}]`}
                        </p>
                      </div>
                      <button
                        className="px-3 text-gray-400 hover:text-red-500 hover:bg-red-50"
                        onClick={() => setReplyingTo(null)}
                        title="Cancelar resposta"
                        data-testid="btn-cancel-reply">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {/* Recording state */}
                  {isRecording ? (
                    <div className="flex items-center gap-3" data-testid="recording-bar">
                      <Button size="sm" variant="ghost" className="h-9 w-9 p-0 rounded-full text-red-500 hover:bg-red-50" onClick={cancelRecording} data-testid="btn-cancel-recording">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <div className="flex-1 flex items-center gap-2">
                        <div className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-sm font-mono text-red-600">{fmtRecTime(recordingTime)}</span>
                        <div className="flex-1 h-1 bg-red-100 rounded-full overflow-hidden">
                          <div className="h-full bg-red-400 rounded-full animate-pulse" style={{ width: `${Math.min((recordingTime / 120) * 100, 100)}%` }} />
                        </div>
                      </div>
                      <Button size="sm" className="h-9 w-9 p-0 rounded-full bg-green-600 hover:bg-green-700" onClick={stopRecording} data-testid="btn-stop-recording">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      {/* Emoji button */}
                      <Button size="sm" variant="ghost" className="h-9 w-9 p-0 rounded-full" onClick={() => { setShowEmoji(!showEmoji); setShowFlowPicker(false); }} data-testid="btn-emoji">
                        <Smile className="h-5 w-5 text-gray-500" />
                      </Button>
                      {/* Attachment button */}
                      <Button size="sm" variant="ghost" className="h-9 w-9 p-0 rounded-full" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="btn-attach">
                        {uploading ? <Loader2 className="h-5 w-5 text-gray-400 animate-spin" /> : <Paperclip className="h-5 w-5 text-gray-500" />}
                      </Button>
                      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload}
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip" />
                      {/* Flow selector button */}
                      <Button size="sm" variant="ghost" className="h-9 w-9 p-0 rounded-full" onClick={() => { setShowFlowPicker(!showFlowPicker); setShowEmoji(false); }} data-testid="btn-flow-picker">
                        <GitBranch className="h-5 w-5 text-gray-500" />
                      </Button>
                      {/* Quick replies */}
                      <Button size="sm" variant="ghost" className="h-9 w-9 p-0 rounded-full" onClick={() => setShowRespostas(true)} data-testid="btn-respostas-rapidas">
                        <Zap className="h-5 w-5 text-gray-500" />
                      </Button>
                      {/* Interactive Buttons */}
                      <Button size="sm" variant="ghost" className="h-9 w-9 p-0 rounded-full" onClick={() => { setShowBotoesModal(true); setShowEmoji(false); setShowFlowPicker(false); }} data-testid="btn-open-botoes-modal">
                        <MousePointerClick className="h-5 w-5 text-gray-500" />
                      </Button>
                      {/* Message Input - Textarea for multi-line */}
                      <Textarea className="flex-1 min-h-[80px] max-h-[200px] rounded-xl bg-white text-sm resize-none py-2 px-3 leading-relaxed" placeholder="Mensagem... (Shift+Enter para quebrar linha)" value={msgInput}
                        onChange={e => { setMsgInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px'; }}
                        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !enviandoMensagem) { e.preventDefault(); enviarMensagem(); } }}
                        onPaste={async (e) => {
                          const items = e.clipboardData?.items;
                          if (!items) return;
                          for (const item of items) {
                            if (item.type.startsWith("image/")) {
                              e.preventDefault();
                              const file = item.getAsFile();
                              if (file && selectedTicket) {
                                setUploading(true);
                                try {
                                  const formData = new FormData();
                                  formData.append("file", file, `print_${Date.now()}.png`);
                                  const uploadRes = await axios.post(`${API}/wpp/chat/upload`, formData);
                                  const { url, tipo, filename } = uploadRes.data;
                                  const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
                                  await axios.post(`${API}/wpp/tickets/${replyTicketId}/mensagens`, {
                                    corpo: "", direcao: "saida", tipo: tipo,
                                    media_url: url, media_tipo: tipo, media_filename: filename,
                                    remetente_nome: usuario.nome || "Atendente", remetente_id: usuario.id || ""
                                  });
                                  setReplyTargetTicketId(null);
                                  await recarregarConversaAtual(); carregarTudo();
                                  toast.success("Imagem enviada!");
                                } catch { toast.error("Erro ao enviar imagem"); }
                                finally { setUploading(false); }
                              }
                              return;
                            }
                          }
                        }}
                        rows={1}
                        data-testid="input-mensagem-wpp" />
                      {/* Send or Mic button */}
                      {msgInput.trim() ? (
                        <Button size="sm" className="h-9 w-9 p-0 rounded-full bg-green-600 hover:bg-green-700" onClick={enviarMensagem} disabled={enviandoMensagem || !msgInput.trim()} data-testid="btn-enviar-msg-wpp">
                          {enviandoMensagem ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      ) : (
                        <Button size="sm" className="h-9 w-9 p-0 rounded-full bg-green-600 hover:bg-green-700" onClick={startRecording} data-testid="btn-mic">
                          <Mic className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right Info Panel */}
          {selectedTicket && (
            <div className="w-[260px] border-l bg-white shrink-0 overflow-y-auto hidden xl:block">
              <div className="p-3 border-b">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Cliente</span>
                  {selectedTicket.lid_verificado === true && <span className="text-[9px] font-semibold text-green-700">Vinculado ✓</span>}
                </div>
                {/* Phone Number (editable - real number) */}
                <div className="mt-3 text-left px-2">
                  <label className="text-[9px] font-bold text-blue-500 uppercase tracking-wider">Telefone</label>
                  <div className="group relative mt-0.5">
                    <input
                      type="text"
                      className="text-sm bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none w-full py-0.5 transition-colors font-semibold text-gray-800"
                      value={selectedTicket.contato_telefone || ""}
                      onChange={e => setSelectedTicket(p => ({ ...p, contato_telefone: e.target.value }))}
                      onBlur={() => {
                        if (selectedTicket) {
                          const updateData = { contato_telefone: selectedTicket.contato_telefone };
                          axios.put(`${API}/wpp/tickets/${selectedTicket.id}`, updateData).then(() => {
                            if (selectedTicket.jid_original && selectedTicket.jid_original.endsWith("@lid") && !isLidNumber(selectedTicket.contato_telefone)) {
                              axios.post(`${API}/wpp/baileys/lid-resolvido`, {
                                lid_jid: selectedTicket.jid_original,
                                telefone: selectedTicket.contato_telefone
                              }).catch(() => {});
                              toast.success("Telefone associado ao LID!");
                            }
                            carregarTudo();
                          }).catch(() => {});
                        }
                      }}
                      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                      placeholder="5591999999999"
                      data-testid="edit-contato-telefone"
                    />
                    <Pencil className="h-3 w-3 text-gray-300 absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                  </div>
                </div>
                {/* Etiquetas pertencem a conversa e ficam disponiveis mesmo sem cliente vinculado. */}
                <div className="relative mt-3 px-2 py-2 border border-gray-200 rounded-lg bg-gray-50" data-testid="ticket-tags-panel">
                  <Label className="text-[9px] text-gray-500">Etiquetas da conversa</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(selectedTicket.tags || []).map(t => { const tObj = tags.find(tg => tg.nome === t); return (
                      <span key={t} className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: tObj?.cor || "#8b5cf6" }}>
                        {t}<button type="button" onClick={() => removeTagTicket(selectedTicket.id, t)} aria-label={`Remover ${t}`}><X className="h-2.5 w-2.5" /></button>
                      </span>
                    ); })}
                    <button type="button" className="text-[9px] px-1.5 py-0.5 rounded border border-dashed border-gray-300 text-gray-600 bg-white hover:border-green-400" onClick={() => setTagPickerFor(tagPickerFor === selectedTicket.id ? null : selectedTicket.id)}>+ Adicionar</button>
                  </div>
                  {tagPickerFor === selectedTicket.id && (
                    <div className="absolute right-1 top-full z-50 mt-1 bg-white border shadow-lg rounded-lg p-1.5 min-w-[210px]">
                      <input type="text" className="w-full text-[10px] border rounded px-2 py-1 mb-1" placeholder="Buscar etiqueta..." value={tagSearchTerm} onChange={e => setTagSearchTerm(e.target.value)} autoFocus />
                      <div className="max-h-48 overflow-y-auto">
                        {tags.filter(t => !(selectedTicket.tags || []).includes(t.nome) && (!tagSearchTerm || t.nome.toLowerCase().includes(tagSearchTerm.toLowerCase()))).map(t => (
                          <button key={t.id} className="flex items-center gap-1.5 w-full px-2 py-1 rounded text-[10px] hover:bg-gray-100 text-left" onClick={() => { addTagTicket(selectedTicket.id, t.nome); setTagPickerFor(null); setTagSearchTerm(""); }}>
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.cor }} />{t.nome}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* Cliente Vinculado */}
                {clienteVinculado ? (
                  <div className="mt-3 px-2 py-2 bg-green-50 border border-green-200 rounded-lg relative">
                    {clienteVinculado.nome && (
                      <div className="mb-2 pb-2 border-b border-green-200">
                        <span className="text-[9px] text-gray-500">Nome</span>
                        <p className="text-xs font-bold text-gray-900 leading-snug" data-testid="cliente-nome-visivel">{clienteVinculado.nome}</p>
                      </div>
                    )}
                    {clienteVinculado.cpf && (
                      <div className="mb-1">
                        <span className="text-[9px] text-gray-500">CPF</span>
                        <p className="text-sm font-bold text-gray-900" data-testid="cliente-cpf-completo">{formatCpf(clienteVinculado.cpf)}</p>
                      </div>
                    )}
                    {calcularIdade(clienteVinculado.data_nascimento) !== null && (
                      <div className="mb-1">
                        <span className="text-[9px] text-gray-500">Idade</span>
                        <p className="text-sm font-bold text-gray-900" data-testid="cliente-idade">{calcularIdade(clienteVinculado.data_nascimento)} anos</p>
                      </div>
                    )}
                    {clienteVinculado.instituicao && (
                      <div className="mb-1">
                        <span className="text-[9px] text-gray-500">Instituição</span>
                        <p className="text-[11px] font-semibold text-gray-700">{clienteVinculado.instituicao}</p>
                      </div>
                    )}
                    {clienteVinculado.matricula && (
                      <div className="mb-1">
                        <span className="text-[9px] text-gray-500">Matrícula</span>
                        <p className="text-[11px] font-semibold text-gray-700">{clienteVinculado.matricula}</p>
                      </div>
                    )}
                    {clienteVinculado.especie_beneficio && (
                      <div className="mb-1">
                        <span className="text-[9px] text-gray-500">Espécie</span>
                        <p className="text-[11px] text-gray-700">{clienteVinculado.especie_beneficio}</p>
                      </div>
                    )}
                    {(clienteVinculado.margem_consignavel || clienteVinculado.margem_cartao_rmc) && (
                      <div className="mb-1 flex gap-2">
                        {clienteVinculado.margem_consignavel && (
                          <div>
                            <span className="text-[9px] text-gray-500">Margem</span>
                            <p className="text-[11px] font-bold text-green-700">R$ {Number(clienteVinculado.margem_consignavel).toFixed(2)}</p>
                          </div>
                        )}
                        {clienteVinculado.margem_cartao_rmc && (
                          <div>
                            <span className="text-[9px] text-gray-500">RMC</span>
                            <p className="text-[11px] font-bold text-blue-700">R$ {Number(clienteVinculado.margem_cartao_rmc).toFixed(2)}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {clienteVinculado.segunda_instituicao && (
                      <div className="mb-1">
                        <span className="text-[9px] text-gray-500">2ª Instituição</span>
                        <p className="text-[11px] font-semibold text-gray-700">{clienteVinculado.segunda_instituicao}</p>
                      </div>
                    )}
                    {valoresDispWpp && valoresDispWpp.tem_calculo && (
                      <div className="mt-2 pt-2 border-t border-green-200" data-testid="wpp-valores-disponiveis">
                        <label className="text-[9px] font-bold text-violet-700 uppercase tracking-wider">💰 Valores disponíveis{valoresDispWpp.fonte === "simulacao" && valoresDispWpp.banco ? ` · ${valoresDispWpp.banco}` : ""}</label>
                        <div className="mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5">
                          {(valoresDispWpp.opcoes || []).map((o) => (
                            <div key={o.prazo} className="flex items-center justify-between text-[10px] border-b border-green-100 py-0.5" data-testid={`wpp-vd-${o.prazo}`}>
                              <span className="text-gray-500">{o.prazo}x</span>
                              <span className="font-bold text-emerald-700">{(o.valor_disponivel || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-[9px] text-gray-400 mt-0.5">{`Parcela ${(valoresDispWpp.margem || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}/mês · oferta aprovada`}</p>
                      </div>
                    )}
                    {clienteVinculado.contratos_internos && clienteVinculado.contratos_internos.length > 0 ? (
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <label className="text-[9px] font-bold text-blue-700 uppercase tracking-wider">Contratos Internos (30d)</label>
                        {clienteVinculado.contratos_internos.map((ct, idx) => (
                          <div key={idx} className="mt-1 px-2 py-1 bg-blue-50 rounded border border-blue-100 flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                ct.situacao === "APROVADO" || ct.situacao === "PAGO" ? "bg-green-100 text-green-700" :
                                ct.situacao === "REPROVADO" || ct.situacao === "CANCELADO" ? "bg-red-100 text-red-700" :
                                ct.situacao === "FLUXO PORTABILIDADE" ? "bg-indigo-100 text-indigo-700" :
                                ct.situacao === "EM PRODUCAO" ? "bg-orange-100 text-orange-700" :
                                ct.situacao === "EM ANALISE" || ct.situacao === "DIGITADO" ? "bg-yellow-100 text-yellow-700" :
                                "bg-blue-100 text-blue-700"
                              }`} data-testid={`contrato-status-${idx}`}>{(ct.situacao || "N/A").replace(/_/g, " ")}</span>
                              {ct.banco && <span className="text-[9px] text-gray-400">{ct.banco}</span>}
                            </div>
                            {ct.updated_at && <span className="text-[9px] text-gray-400">{new Date(ct.updated_at).toLocaleDateString("pt-BR")}</span>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Contrato Interno (30d)</label>
                        <p className="text-[10px] text-gray-400 mt-0.5">Nenhum contrato recente</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-3 px-2 py-2 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                    <p className="text-[10px] text-gray-400 text-center mb-2">Nenhum cliente vinculado</p>
                    <div className="flex gap-1">
                      <Input
                        type="text"
                        placeholder="Buscar por CPF..."
                        className="h-7 text-xs"
                        data-testid="input-cpf-vincular"
                        onKeyDown={async (e) => {
                          if (e.key === "Enter") {
                            await vincularCpfSelecionado(e.target.value);
                          }
                        }}
                      />
                      <Button type="button" size="sm" className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700" data-testid="btn-buscar-cpf"
                        onClick={async () => {
                          const input = document.querySelector('[data-testid="input-cpf-vincular"]');
                          const cpf = input?.value?.trim();
                          if (!cpf) return;
                          await vincularCpfSelecionado(cpf);
                        }}>
                        <Search className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {/* Ações rápidas do cliente */}
              {clienteVinculado && (
                <div className="px-3 py-2 flex items-center justify-center gap-3 border-b">
                  <a href={`/vendas?cpf=${encodeURIComponent(clienteVinculado.cpf || '')}`} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center" data-testid="wpp-btn-contratos">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-purple-600 hover:text-purple-700 hover:bg-purple-50" title="Ver Contratos">
                      <FileText className="h-4 w-4" />
                    </Button>
                    <span className="text-[9px] text-purple-600 font-medium">Contratos</span>
                  </a>
                  <a href={`/monitoramento-contratos?cliente_id=${clienteVinculado.id}`} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center" data-testid="wpp-btn-monitorar">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50" title="Monitorar">
                      <FileSearch className="h-4 w-4" />
                    </Button>
                    <span className="text-[9px] text-green-600 font-medium">Monitorar</span>
                  </a>
                  <a href={`/clientes/${clienteVinculado.id}/editar`} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center" data-testid="wpp-btn-editar">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50" title="Editar Cliente">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <span className="text-[9px] text-amber-600 font-medium">Editar</span>
                  </a>
                </div>
              )}
              <ProspectaSimulacaoModal
                open={showProspecta}
                onClose={() => setShowProspecta(false)}
                cpf={selectedTicket?.cpf_contato || selectedTicket?.pabx_data?.cpf || clienteVinculado?.cpf}
                telefone={selectedTicket?.contato_telefone}
                nome={clienteVinculado?.nome || selectedTicket?.contato_nome}
                cliente={clienteVinculado}
              />
              <div className="p-3">
                <details className="group" data-testid="painel-mais-detalhes">
                  <summary className="cursor-pointer list-none flex items-center justify-between text-xs font-semibold text-gray-600 hover:text-gray-900">
                    <span>Mais detalhes</span>
                    <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="mt-3 space-y-3">
                    {timelineResumo && (
                      <div className="rounded-md border border-sky-100 bg-sky-50/60 px-2 py-1.5" data-testid="timeline-omnichannel-summary">
                        <p className="text-[10px] font-semibold text-sky-800">Linha do tempo unificada</p>
                        <p className="text-[10px] text-gray-500">
                          {timelineResumo.total_conversas} conversa{timelineResumo.total_conversas === 1 ? "" : "s"} · {timelineResumo.canais.length} {timelineResumo.canais.length === 1 ? "canal" : "canais"}
                        </p>
                      </div>
                    )}
                    <button onClick={() => verificarWhatsApp(selectedTicket.id)} disabled={verificandoWpp}
                      className="text-[10px] font-medium text-sky-600 hover:text-sky-800 underline disabled:opacity-50" data-testid="btn-verificar-whatsapp">
                      {verificandoWpp ? "Verificando..." : "Verificar WhatsApp"}
                    </button>
                    {selectedTicket.jid_original && (
                      <div><Label className="text-[10px] text-gray-400">Identificador técnico</Label><p className="text-[10px] text-gray-400 font-mono break-all">{selectedTicket.jid_original}</p></div>
                    )}
                    {clienteVinculado?.tags_contrato?.length > 0 && (
                      <div><Label className="text-[10px] text-gray-400">Contrato</Label><div className="flex flex-wrap gap-1 mt-1">{clienteVinculado.tags_contrato.map(t => <span key={t} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">{t}</span>)}</div></div>
                    )}
                    <div><Label className="text-[10px] text-gray-400">Notas</Label>
                      <Textarea className="mt-1 text-xs h-28 resize-y" value={selectedTicket.notas || ""} placeholder="Adicionar nota..." onChange={e => setSelectedTicket(p => ({ ...p, notas: e.target.value }))} onBlur={() => { if (selectedTicket) axios.put(`${API}/wpp/tickets/${selectedTicket.id}`, { notas: selectedTicket.notas }).catch(() => {}); }} />
                    </div>
                    <div data-testid="timeline-tempos"><Label className="text-[10px] text-gray-400 flex items-center gap-1"><Clock className="h-3 w-3" /> Histórico</Label>
                      {ticketTimeline && (ticketTimeline.historico || []).length > 0 ? (
                        <div className="mt-1 text-[10px] text-gray-500">{ticketTimeline.total_sessoes} {ticketTimeline.total_sessoes === 1 ? "sessão" : "sessões"} · média {fmtDuracao(ticketTimeline.tempo_medio_segundos)} · total {fmtDuracao(ticketTimeline.tempo_total_segundos)}</div>
                      ) : <p className="text-[10px] text-gray-300 mt-1">Nenhum registro</p>}
                    </div>
                  </div>
                </details>
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {/* === TAB: TAGS === */}
      {tab === "tags" && (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-4xl mx-auto space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <h2 className="text-lg font-bold text-gray-800">Tags</h2>
              <div className="flex gap-2 flex-wrap">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                  <Input
                    value={tagSearch}
                    onChange={e => setTagSearch(e.target.value)}
                    placeholder="Buscar tag..."
                    className="h-8 w-48 pl-7 text-xs"
                    data-testid="input-buscar-tag"
                  />
                </div>
                {tagCategorias.length === 0 && (
                  <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={seedCategorias} data-testid="btn-seed-categorias">
                    Auto-organizar
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setShowCategoriaManager(true)} data-testid="btn-gerenciar-categorias">
                  <ListFilter className="h-3.5 w-3.5" /> Categorias
                </Button>
                <Button size="sm" className="h-8 bg-purple-600 hover:bg-purple-700 gap-1 text-xs" onClick={() => setShowNovaTag(true)} data-testid="btn-nova-tag">
                  <Plus className="h-3.5 w-3.5" /> Nova Tag
                </Button>
              </div>
            </div>

            {tags.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-gray-400"><Tag className="h-10 w-10 mx-auto mb-2 opacity-30" /><p>Nenhuma tag criada</p><p className="text-sm">Crie tags como STILOCRED, VENDAS, VIP</p></CardContent></Card>
            ) : (() => {
              const tagsFiltradas = tags.filter(t => !tagSearch || t.nome.toLowerCase().includes(tagSearch.toLowerCase()));
              const grupos = [
                ...tagCategorias.map(cat => ({
                  id: cat.id,
                  nome: cat.nome,
                  cor: cat.cor,
                  removable: true,
                  tags: tagsFiltradas.filter(t => t.categoria_id === cat.id)
                })),
                {
                  id: "__sem_categoria__",
                  nome: "SEM CATEGORIA",
                  cor: "#9ca3af",
                  removable: false,
                  tags: tagsFiltradas.filter(t => !t.categoria_id || !tagCategorias.find(c => c.id === t.categoria_id))
                }
              ].filter(g => g.tags.length > 0 || (g.id !== "__sem_categoria__"));

              if (grupos.every(g => g.tags.length === 0)) {
                return <Card><CardContent className="py-6 text-center text-gray-400 text-sm">Nenhuma tag encontrada para "{tagSearch}"</CardContent></Card>;
              }

              return grupos.map(grupo => {
                const isCollapsed = collapsedCats[grupo.id];
                return (
                  <div key={grupo.id} data-testid={`grupo-categoria-${grupo.id}`}>
                    <div className="flex items-center justify-between mb-2 pb-1 border-b-2" style={{ borderColor: grupo.cor }}>
                      <button
                        className="flex items-center gap-2 font-bold text-sm hover:opacity-75 transition"
                        style={{ color: grupo.cor }}
                        onClick={() => setCollapsedCats(prev => ({ ...prev, [grupo.id]: !prev[grupo.id] }))}
                        data-testid={`toggle-cat-${grupo.id}`}
                      >
                        <ChevronDown className={`h-4 w-4 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                        <span>{grupo.nome}</span>
                        <Badge className="bg-gray-100 text-gray-600 text-[10px]">{grupo.tags.length}</Badge>
                      </button>
                      {grupo.removable && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditCategoria({ ...tagCategorias.find(c => c.id === grupo.id) })} data-testid={`btn-edit-cat-${grupo.id}`}>
                            <Edit2 className="h-3 w-3 text-gray-400" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => excluirCategoria(grupo.id)} data-testid={`btn-del-cat-${grupo.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {!isCollapsed && (
                      grupo.tags.length === 0 ? (
                        <p className="text-[11px] text-gray-400 italic mb-4 pl-6">Nenhuma tag nesta categoria</p>
                      ) : (
                        <div className="grid gap-2 md:grid-cols-2 mb-4">
                          {grupo.tags.map(t => (
                            <Card key={t.id} className="border-l-4" style={{ borderLeftColor: t.cor }} data-testid={`tag-card-${t.id}`}>
                              <CardContent className="py-3 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.cor + "20" }}>
                                    <Tag className="h-4 w-4" style={{ color: t.cor }} />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="font-bold text-sm truncate" style={{ color: t.cor }}>{t.nome}</p>
                                    <p className="text-[10px] text-gray-400">{tickets.filter(tk => (tk.tags || []).includes(t.nome)).length} tickets</p>
                                  </div>
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Mover para categoria" data-testid={`btn-move-tag-${t.id}`}>
                                        <ListFilter className="h-3.5 w-3.5 text-gray-400" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-56 p-1" align="end">
                                      <div className="text-[10px] font-bold text-gray-500 px-2 py-1">MOVER PARA:</div>
                                      {tagCategorias.map(cat => (
                                        <button key={cat.id}
                                          className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 flex items-center gap-2 ${t.categoria_id === cat.id ? "bg-gray-100 font-bold" : ""}`}
                                          onClick={() => moverTagParaCategoria(t.id, cat.id)}
                                          data-testid={`move-to-${cat.id}`}
                                        >
                                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: cat.cor }} />
                                          {cat.nome}
                                        </button>
                                      ))}
                                      <button
                                        className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 flex items-center gap-2 text-gray-500 ${!t.categoria_id ? "bg-gray-100 font-bold" : ""}`}
                                        onClick={() => moverTagParaCategoria(t.id, null)}
                                      >
                                        <span className="h-2 w-2 rounded-full bg-gray-400" />
                                        Sem Categoria
                                      </button>
                                    </PopoverContent>
                                  </Popover>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setShowEditTag({ ...t })} data-testid={`btn-edit-tag-${t.id}`}><Edit2 className="h-3.5 w-3.5 text-gray-400" /></Button>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => excluirTag(t.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* === TAB: FILAS === */}
      {tab === "filas" && (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-2xl mx-auto space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-800">Filas de Atendimento</h2>
              <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1" onClick={() => setShowNovaFila(true)} data-testid="btn-nova-fila"><Plus className="h-4 w-4" /> Nova Fila</Button>
            </div>
            {filas.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-gray-400"><Users className="h-10 w-10 mx-auto mb-2 opacity-30" /><p>Nenhuma fila</p></CardContent></Card>
            ) : filas.map(f => (
              <Card key={f.id} className="border-l-4" style={{ borderLeftColor: f.cor }}>
                <CardContent className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: f.cor + "20" }}><Users className="h-4 w-4" style={{ color: f.cor }} /></div>
                    <div><p className="font-semibold text-sm">{f.nome}</p>{f.descricao && <p className="text-xs text-gray-500">{f.descricao}</p>}</div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-400 h-7 w-7 p-0" onClick={() => excluirFila(f.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* === TAB: RESPOSTAS === */}
      {tab === "respostas" && (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-2xl mx-auto space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-800">Respostas Rapidas</h2>
              <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1" onClick={() => setShowNovaResposta(true)} data-testid="btn-nova-resposta"><Plus className="h-4 w-4" /> Nova Resposta</Button>
            </div>
            {respostas.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-gray-400"><Zap className="h-10 w-10 mx-auto mb-2 opacity-30" /><p>Nenhuma resposta</p></CardContent></Card>
            ) : respostas.map(r => (
              <Card key={r.id}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2"><Badge className="bg-green-100 text-green-700 text-xs">/{r.atalho}</Badge><Badge variant="outline" className="text-xs">{r.categoria}</Badge></div>
                    <Button size="sm" variant="ghost" className="text-red-400 h-7 w-7 p-0" onClick={() => excluirResposta(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                  <p className="text-sm text-gray-700">{r.mensagem}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* === TAB: DASHBOARD === */}
      {tab === "dashboard" && (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-4xl mx-auto space-y-4">
            <h2 className="text-lg font-bold text-gray-800">Dashboard</h2>
            <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
              {[
                { label: "Aguardando", val: dashboard.abertos || 0, color: "yellow", Icon: Clock },
                { label: "Em Atendimento", val: dashboard.em_atendimento || 0, color: "blue", Icon: MessageCircle },
                { label: "Negociacao", val: dashboard.resolvidos || 0, color: "green", Icon: CheckCheck },
                { label: "Mensagens", val: dashboard.total_mensagens || 0, color: "purple", Icon: MessageCircle },
                { label: "Tags", val: dashboard.total_tags || 0, color: "indigo", Icon: Tag },
              ].map((s, i) => (
                <Card key={i} className={`border-l-4 border-l-${s.color}-500`}>
                  <CardContent className="pt-3 pb-2 px-3"><p className="text-xs text-gray-500">{s.label}</p><p className={`text-2xl font-bold text-${s.color}-700`}>{s.val}</p></CardContent>
                </Card>
              ))}
            </div>
            <Card>
              <CardHeader><CardTitle className="text-base">Conexao WhatsApp</CardTitle></CardHeader>
              <CardContent>
                {baileysStatus.status === "connected" ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center"><Check className="h-6 w-6 text-green-600" /></div>
                        <div>
                          <p className="font-semibold text-green-800">WhatsApp Conectado</p>
                          <p className="text-sm text-green-600">{baileysStatus.number ? baileysStatus.number.split("@")[0] : "Numero conectado"}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="text-red-500 border-red-300 hover:bg-red-50" onClick={disconnectBaileys} data-testid="btn-disconnect-wpp">Desconectar</Button>
                    </div>
                  </div>
                ) : baileysQR ? (
                  <div className="text-center p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Escaneie o QR Code com seu WhatsApp</p>
                    <img src={baileysQR} alt="QR Code WhatsApp" className="mx-auto w-64 h-64 border rounded-lg shadow" data-testid="qr-code-img" />
                    <p className="text-xs text-gray-500 mt-3">Abra o WhatsApp &gt; Configuracoes &gt; Aparelhos conectados &gt; Conectar dispositivo</p>
                    <Button size="sm" variant="outline" className="mt-3" onClick={() => { setBaileysQR(null); if (qrPollRef.current) clearInterval(qrPollRef.current); }}>Cancelar</Button>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <QrCode className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="font-semibold text-gray-700 mb-1">WhatsApp Desconectado</p>
                    <p className="text-sm text-gray-500 mb-4">Conecte seu WhatsApp para enviar e receber mensagens em tempo real</p>
                    <div className="flex gap-2 justify-center">
                      <Button className="bg-green-600 hover:bg-green-700 gap-2" onClick={startQRPolling} disabled={loadingQR} data-testid="btn-connect-wpp">
                        {loadingQR ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
                        {loadingQR ? "Gerando QR..." : "Conectar WhatsApp"}
                      </Button>
                      <Button variant="outline" onClick={checkBaileysStatus}><RefreshCw className="h-4 w-4" /></Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="mt-2" data-testid="dashboard-gestao-conexoes">
            <GestaoConexoesWhatsApp />
          </div>
        </div>
      )}




      {/* === TAB: FLOWS === */}
      {tab === "flows" && (
        <div className="flex-1 overflow-hidden">
          <FlowBuilder onBack={() => setTab("kanban")} />
        </div>
      )}

      {/* ======= MODALS ======= */}

      {/* Buttons Composer Modal */}
      <Dialog open={showBotoesModal} onOpenChange={setShowBotoesModal}>
        <DialogContent className="max-w-md" data-testid="modal-botoes-interativos">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MousePointerClick className="h-5 w-5 text-cyan-600" /> Enviar Botoes Interativos</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-gray-500">Mensagem *</Label>
              <Textarea className="h-20 text-sm mt-1" placeholder="Texto principal da mensagem..." value={botoesForm.corpo}
                onChange={e => setBotoesForm(p => ({ ...p, corpo: e.target.value }))} data-testid="input-botoes-corpo" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Rodape (opcional)</Label>
              <Input className="h-8 text-sm mt-1" placeholder="Texto do rodape..." value={botoesForm.footer}
                onChange={e => setBotoesForm(p => ({ ...p, footer: e.target.value }))} data-testid="input-botoes-footer" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-2 block">Botoes (max 3)</Label>
              {botoesForm.buttons.map((btn, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <div className="h-6 w-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                  <Input className="h-8 text-sm flex-1" placeholder={`Texto do botao ${i + 1}...`} value={btn.label}
                    onChange={e => { const btns = [...botoesForm.buttons]; btns[i] = { label: e.target.value }; setBotoesForm(p => ({ ...p, buttons: btns })); }}
                    data-testid={`input-botao-${i}`} />
                  {botoesForm.buttons.length > 1 && (
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                      onClick={() => setBotoesForm(p => ({ ...p, buttons: p.buttons.filter((_, j) => j !== i) }))} data-testid={`btn-remove-botao-${i}`}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              {botoesForm.buttons.length < 3 && (
                <Button size="sm" variant="outline" className="w-full h-8 text-xs border-dashed text-cyan-600 border-cyan-300 hover:bg-cyan-50"
                  onClick={() => setBotoesForm(p => ({ ...p, buttons: [...p.buttons, { label: "" }] }))} data-testid="btn-add-botao">
                  <Plus className="h-3 w-3 mr-1" /> Adicionar Botao
                </Button>
              )}
            </div>
            {/* Preview */}
            {botoesForm.corpo.trim() && (
              <div className="border rounded-lg overflow-hidden bg-[#efeae2] p-3">
                <p className="text-[10px] text-gray-400 mb-1.5 font-medium">Pre-visualizacao</p>
                <div className="bg-[#d9fdd3] rounded-2xl rounded-tr-sm overflow-hidden max-w-[260px] ml-auto">
                  <div className="px-3 py-1.5 text-sm">
                    <p className="whitespace-pre-wrap break-words">{botoesForm.corpo}</p>
                    {botoesForm.footer && <p className="text-[10px] text-gray-400 mt-1">{botoesForm.footer}</p>}
                  </div>
                  {botoesForm.buttons.filter(b => b.label.trim()).length > 0 && (
                    <div className="border-t">
                      {botoesForm.buttons.filter(b => b.label.trim()).map((btn, i) => (
                        <div key={i} className={`text-center py-2 px-3 text-[13px] font-medium text-[#00a5f4] ${i < botoesForm.buttons.filter(b => b.label.trim()).length - 1 ? "border-b" : ""}`}>
                          <MousePointerClick className="h-3 w-3 inline mr-1.5 opacity-60" />{btn.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBotoesModal(false)}>Cancelar</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700 gap-1.5" onClick={enviarBotoes} disabled={enviandoBotoes} data-testid="btn-enviar-botoes">
              {enviandoBotoes ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Enviar Botoes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNovoTicket} onOpenChange={setShowNovoTicket}>
        <DialogContent className="max-w-sm" data-testid="modal-novo-ticket">
          <DialogHeader><DialogTitle>Nova Conversa</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Nome *</Label><Input value={novoTicketForm.contato_nome} onChange={e => setNovoTicketForm({ ...novoTicketForm, contato_nome: e.target.value })} placeholder="Joao Silva" data-testid="input-ticket-nome" /></div>
            <div><Label>Telefone *</Label><Input value={novoTicketForm.contato_telefone} onChange={e => setNovoTicketForm({ ...novoTicketForm, contato_telefone: e.target.value })} placeholder="11999998888" data-testid="input-ticket-tel" /></div>
            <div><Label>Fila</Label><Select value={novoTicketForm.fila_id} onValueChange={v => setNovoTicketForm({ ...novoTicketForm, fila_id: v })}><SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger><SelectContent><SelectItem value=" ">Sem fila</SelectItem>{filas.map(f => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowNovoTicket(false)}>Cancelar</Button><Button className="bg-green-600 hover:bg-green-700" onClick={criarTicket} data-testid="btn-criar-ticket">Criar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNovaFila} onOpenChange={setShowNovaFila}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nova Fila</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Nome *</Label><Input value={novaFilaForm.nome} onChange={e => setNovaFilaForm({ ...novaFilaForm, nome: e.target.value })} placeholder="Vendas" data-testid="input-fila-nome" /></div>
            <div><Label>Cor</Label><Input type="color" value={novaFilaForm.cor} onChange={e => setNovaFilaForm({ ...novaFilaForm, cor: e.target.value })} className="h-10 w-20" /></div>
            <div><Label>Descricao</Label><Input value={novaFilaForm.descricao} onChange={e => setNovaFilaForm({ ...novaFilaForm, descricao: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowNovaFila(false)}>Cancelar</Button><Button className="bg-green-600 hover:bg-green-700" onClick={criarFila} data-testid="btn-criar-fila">Criar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNovaTag} onOpenChange={setShowNovaTag}>
        <DialogContent className="max-w-sm" data-testid="modal-nova-tag">
          <DialogHeader><DialogTitle>Nova Tag</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Nome *</Label><Input value={novaTagForm.nome} onChange={e => setNovaTagForm({ ...novaTagForm, nome: e.target.value })} placeholder="STILOCRED" data-testid="input-tag-nome" /></div>
            <div><Label>Cor</Label><Input type="color" value={novaTagForm.cor} onChange={e => setNovaTagForm({ ...novaTagForm, cor: e.target.value })} className="h-10 w-20" /></div>
            <div>
              <Label>Categoria</Label>
              <Select value={novaTagForm.categoria_id || "__none__"} onValueChange={v => setNovaTagForm({ ...novaTagForm, categoria_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="select-nova-tag-categoria"><SelectValue placeholder="Sem categoria" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem categoria</SelectItem>
                  {tagCategorias.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span>Preview:</span>
              <span className="text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: novaTagForm.cor + "20", color: novaTagForm.cor }}>{novaTagForm.nome || "TAG"}</span>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowNovaTag(false)}>Cancelar</Button><Button className="bg-purple-600 hover:bg-purple-700" onClick={criarTag} data-testid="btn-criar-tag">Criar Tag</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showEditTag} onOpenChange={() => setShowEditTag(null)}>
        <DialogContent className="max-w-sm" data-testid="modal-edit-tag">
          <DialogHeader><DialogTitle>Editar Tag</DialogTitle></DialogHeader>
          {showEditTag && (
            <div className="space-y-3 py-2">
              <div><Label>Nome</Label><Input value={showEditTag.nome} onChange={e => setShowEditTag({ ...showEditTag, nome: e.target.value })} /></div>
              <div><Label>Cor</Label><Input type="color" value={showEditTag.cor} onChange={e => setShowEditTag({ ...showEditTag, cor: e.target.value })} className="h-10 w-20" /></div>
              <div>
                <Label>Categoria</Label>
                <Select value={showEditTag.categoria_id || "__none__"} onValueChange={v => setShowEditTag({ ...showEditTag, categoria_id: v === "__none__" ? null : v })}>
                  <SelectTrigger data-testid="select-edit-tag-categoria"><SelectValue placeholder="Sem categoria" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sem categoria</SelectItem>
                    {tagCategorias.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2"><span>Preview:</span><span className="text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: showEditTag.cor + "20", color: showEditTag.cor }}>{showEditTag.nome}</span></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowEditTag(null)}>Cancelar</Button><Button className="bg-purple-600 hover:bg-purple-700" onClick={editarTag}>Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === MODAL: GERENCIAR CATEGORIAS === */}
      <Dialog open={showCategoriaManager} onOpenChange={setShowCategoriaManager}>
        <DialogContent className="max-w-lg" data-testid="modal-categorias">
          <DialogHeader><DialogTitle>Gerenciar Categorias de Tags</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            <div className="flex gap-2 items-end border-b pb-3">
              <div className="flex-1">
                <Label className="text-xs">Nova Categoria</Label>
                <Input value={novaCategoriaForm.nome} onChange={e => setNovaCategoriaForm({ ...novaCategoriaForm, nome: e.target.value })} placeholder="Ex: BENEFICIOS" data-testid="input-nova-categoria-nome" />
              </div>
              <Input type="color" value={novaCategoriaForm.cor} onChange={e => setNovaCategoriaForm({ ...novaCategoriaForm, cor: e.target.value })} className="h-10 w-16" />
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={criarCategoria} data-testid="btn-criar-categoria">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {tagCategorias.length === 0 ? (
              <div className="text-center py-4 text-gray-400 text-sm">
                <p>Nenhuma categoria criada.</p>
                <Button size="sm" variant="outline" className="mt-2 text-xs" onClick={seedCategorias} data-testid="btn-seed-modal">
                  Criar categorias sugeridas
                </Button>
              </div>
            ) : (
              <div className="space-y-1">
                {tagCategorias.map(cat => (
                  <div key={cat.id} className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50" data-testid={`categoria-row-${cat.id}`}>
                    {editCategoria?.id === cat.id ? (
                      <>
                        <Input type="color" value={editCategoria.cor} onChange={e => setEditCategoria({ ...editCategoria, cor: e.target.value })} className="h-8 w-12" />
                        <Input value={editCategoria.nome} onChange={e => setEditCategoria({ ...editCategoria, nome: e.target.value })} className="h-8 text-sm flex-1" />
                        <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700" onClick={salvarEditCategoria}><Check className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" className="h-7" onClick={() => setEditCategoria(null)}><XCircle className="h-3.5 w-3.5" /></Button>
                      </>
                    ) : (
                      <>
                        <span className="h-4 w-4 rounded-full" style={{ backgroundColor: cat.cor }} />
                        <span className="font-semibold text-sm flex-1" style={{ color: cat.cor }}>{cat.nome}</span>
                        <Badge variant="outline" className="text-[10px]">{tags.filter(t => t.categoria_id === cat.id).length} tags</Badge>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditCategoria({ ...cat })}><Edit2 className="h-3.5 w-3.5 text-gray-400" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => excluirCategoria(cat.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCategoriaManager(false)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNovaResposta} onOpenChange={setShowNovaResposta}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nova Resposta Rapida</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Atalho * (ex: /oi)</Label><Input value={novaRespostaForm.atalho} onChange={e => setNovaRespostaForm({ ...novaRespostaForm, atalho: e.target.value })} data-testid="input-resp-atalho" /></div>
            <div><Label>Categoria</Label><Select value={novaRespostaForm.categoria} onValueChange={v => setNovaRespostaForm({ ...novaRespostaForm, categoria: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="geral">Geral</SelectItem><SelectItem value="vendas">Vendas</SelectItem><SelectItem value="suporte">Suporte</SelectItem></SelectContent></Select></div>
            <div><Label>Mensagem *</Label><Textarea value={novaRespostaForm.mensagem} onChange={e => setNovaRespostaForm({ ...novaRespostaForm, mensagem: e.target.value })} className="h-20" data-testid="input-resp-msg" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowNovaResposta(false)}>Cancelar</Button><Button className="bg-green-600 hover:bg-green-700" onClick={criarResposta} data-testid="btn-criar-resposta">Criar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRespostas} onOpenChange={setShowRespostas}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Respostas Rapidas</DialogTitle></DialogHeader>
          <div className="max-h-60 overflow-y-auto space-y-1 py-2">
            {respostas.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">Nenhuma</p> : respostas.map(r => (
              <div key={r.id} className="p-2 rounded-lg border hover:bg-green-50 cursor-pointer" onClick={() => { setMsgInput(r.mensagem); setShowRespostas(false); }}>
                <Badge className="bg-green-100 text-green-700 text-xs mb-0.5">/{r.atalho}</Badge>
                <p className="text-xs text-gray-600">{r.mensagem}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTransferir} onOpenChange={setShowTransferir}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Transferir Conversa</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">{filas.map(f => (
            <Button key={f.id} variant="outline" className="w-full justify-start gap-2" onClick={() => transferirTicket({ fila_id: f.id, fila_nome: f.nome })}>
              <div className="h-3 w-3 rounded-full" style={{ backgroundColor: f.cor }} />{f.nome}
            </Button>
          ))}{filas.length === 0 && <p className="text-sm text-gray-400 text-center">Crie filas primeiro</p>}</div>
        </DialogContent>
      </Dialog>

      {/* Criar Agendamento Modal */}
      <Dialog open={showCriarAgendamento} onOpenChange={setShowCriarAgendamento}>
        <DialogContent className="max-w-lg" data-testid="modal-criar-agendamento">
          <DialogHeader><DialogTitle>Criar agendamento</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {/* Tabs */}
            <div className="flex border-b">
              {[
                { key: "rapidas", label: "Respostas rapidas" },
                { key: "personalizada", label: "Respostas personalizadas" },
                { key: "fluxo", label: "Fluxo" },
              ].map(t => (
                <button key={t.key} onClick={() => {
                  setAgendamentoTab(t.key);
                  if (t.key === "fluxo") setAgendamentoForm(prev => ({ ...prev, tipo: "fluxo" }));
                  else setAgendamentoForm(prev => ({ ...prev, tipo: "mensagem" }));
                }}
                  className={`flex-1 py-2 text-xs font-medium transition-colors ${agendamentoTab === t.key ? "text-green-600 border-b-2 border-green-500" : "text-gray-400 hover:text-gray-600"}`}
                  data-testid={`agendamento-tab-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Content based on tab */}
            {agendamentoTab === "rapidas" && (
              <div className="max-h-48 overflow-y-auto space-y-1" data-testid="agendamento-rapidas">
                {respostas.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">Nenhuma resposta rapida. Crie na aba "Respostas".</p>
                ) : respostas.map(r => (
                  <div key={r.id} className={`p-2 rounded-lg border cursor-pointer transition-colors ${agendamentoForm.conteudo === r.mensagem ? "border-green-500 bg-green-50" : "hover:bg-gray-50"}`}
                    onClick={() => setAgendamentoForm(prev => ({ ...prev, conteudo: r.mensagem, tipo: "mensagem" }))}>
                    <Badge className="bg-green-100 text-green-700 text-[10px] mb-0.5">/{r.atalho}</Badge>
                    <p className="text-xs text-gray-600">{r.mensagem}</p>
                  </div>
                ))}
              </div>
            )}

            {agendamentoTab === "personalizada" && (
              <div data-testid="agendamento-personalizada">
                <Textarea
                  value={agendamentoForm.conteudo}
                  onChange={e => setAgendamentoForm(prev => ({ ...prev, conteudo: e.target.value }))}
                  placeholder="Escreva sua mensagem..."
                  className="min-h-[120px] text-sm"
                  data-testid="input-agendamento-msg"
                />
                <div className="flex items-center gap-1 mt-2">
                  <button className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500"
                    onClick={() => applyFormatting("bold")} title="Negrito"><Bold className="h-3.5 w-3.5" /></button>
                  <button className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500"
                    onClick={() => applyFormatting("italic")} title="Italico"><Italic className="h-3.5 w-3.5" /></button>
                  <button className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500"
                    onClick={() => applyFormatting("strike")} title="Riscado"><Strikethrough className="h-3.5 w-3.5" /></button>
                  <button className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500"
                    onClick={() => setShowEmoji(!showEmoji)} title="Emoji"><Smile className="h-3.5 w-3.5" /></button>
                  <Separator orientation="vertical" className="h-5 mx-1" />
                  <div className="relative">
                    <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-gray-500" data-testid="btn-variaveis"
                      onClick={(e) => {
                        const menu = e.currentTarget.nextElementSibling;
                        if (menu) menu.classList.toggle("hidden");
                      }}>
                      Variaveis
                    </Button>
                    <div className="hidden absolute left-0 top-8 z-50 bg-white border shadow-lg rounded-lg p-1.5 min-w-[140px]">
                      {["nome", "telefone", "atendente", "fila", "data"].map(v => (
                        <button key={v} className="flex items-center gap-1 w-full px-2 py-1 rounded text-xs hover:bg-gray-100 text-left"
                          onClick={() => insertVariable(v)}>
                          <span className="text-purple-600 font-mono">{`{${v}}`}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {agendamentoTab === "fluxo" && (
              <div className="space-y-2" data-testid="agendamento-fluxo">
                {flows.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">Nenhum fluxo criado. Crie na aba "Flows".</p>
                ) : flows.map(f => (
                  <div key={f.id} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${agendamentoForm.flow_id === f.id ? "border-green-500 bg-green-50" : "hover:bg-gray-50"}`}
                    onClick={() => setAgendamentoForm(prev => ({ ...prev, flow_id: f.id, flow_nome: f.nome, tipo: "fluxo" }))}
                    data-testid={`agendamento-flow-${f.id}`}>
                    <GitBranch className="h-4 w-4 text-green-600 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-700 truncate">{f.nome}</p>
                      {f.descricao && <p className="text-[10px] text-gray-400 truncate">{f.descricao}</p>}
                    </div>
                    {agendamentoForm.flow_id === f.id && <Check className="h-4 w-4 text-green-600 ml-auto shrink-0" />}
                  </div>
                ))}
              </div>
            )}

            {/* Date/Time picker */}
            <div>
              <Label className="text-xs text-gray-600">Data do agendamento</Label>
              <Input
                type="datetime-local"
                value={agendamentoForm.data_agendamento}
                onChange={e => setAgendamentoForm(prev => ({ ...prev, data_agendamento: e.target.value }))}
                className="mt-1"
                data-testid="input-datetime-agendamento"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCriarAgendamento(false)}>Fechar</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={criarAgendamento} data-testid="btn-salvar-agendamento">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
